import {
  Badge,
  Button,
  Callout,
  Card,
  Dialog,
  Icon,
  IconButton,
  Input,
  ObsinaProvider,
  Progress,
  Select,
  Skeleton,
  StatusPill,
  Switch,
  ToastRegion,
  useObsinaI18n,
} from "@obsina/design-system";
import type { StatusKind } from "@obsina/design-system";
import {
  AlertTriangle,
  Archive,
  Building2,
  Calendar,
  ChevronDown,
  CircleHelp,
  Cloud,
  ListTree,
  Check,
  Clock3,
  FileCheck2,
  FileText,
  Folder,
  FolderTree,
  FolderOpen,
  Info,
  KeyRound,
  Library,
  Link2,
  LockKeyhole,
  Mail,
  MessageCircle,
  MessagesSquare,
  Network,
  Paperclip,
  Plus,
  RefreshCw,
  Save,
  ShieldCheck,
  Smartphone,
  Unplug,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { LucideIcon } from "lucide-react";
import {
  getControlPlaneSummary,
  getSharePointDriveSelections,
  isVisibilityTransitionResetRequired,
  normalizeApiBase,
  requestHistoryEstimate,
  saveHistoryStart,
  saveVisibility,
  setServiceEnabled,
  startMicrosoftAuthorization,
} from "./api";
import type {
  ConnectionSummary,
  ControlPlaneState,
  ControlPlaneHistoryEstimate,
  ControlPlaneSummary,
  Coverage,
  CoverageMetric,
  SourceKind,
  SourceProcessing,
  SourceType,
  VisibilityClass,
  WhatsAppLinkState,
} from "./domain";
import {
  parseControlPlaneSummary,
  primaryConnection,
  whatsappConnections,
  whatsappLinkState,
} from "./domain";
import { APP_DESIGN_SYSTEM_MESSAGES, APP_LOCALE } from "./locale";
import { enMessages } from "./messages";
import type { AppMessages } from "./messages";
import {
  addWork,
  hasLiveWork,
  hasStoppedWork,
  totalWork,
} from "./processing-counts";
import { authorizeMicrosoftViaPopup } from "./oauth-popup";
import { getPreviewSummary, previewStates } from "./preview";
// The boundary a member picks is built, bounded and printed in one place, so
// the date on screen and the instant behind it cannot drift apart by a zone.
import {
  dateInputToIso,
  formatHistoryRange,
  formatSyncTiming,
  historyDateForDays,
  isAllowedHistoryDate,
  todayDateInput,
} from "./history-range";
import { AppShell } from "./AppShell";
import type { AppView } from "./navigation";
import { PhaseClosedView } from "./PhaseClosedView";
import { MemoryGraphView } from "./MemoryGraphView";
import { SourcesView } from "./sources/SourcesView";
import { IntegrationsView } from "./integrations/IntegrationsView";
import { ConnectAssistantView } from "./integrations/ConnectAssistantView";
import { readConsentRequest } from "./integrations/consent-request";
import type { AccessTokenReader } from "./integrations/session-token";
import { WhatsAppWizard } from "./whatsapp/WhatsAppWizard";
import { createWhatsAppApi } from "./whatsapp/api";
import { whatsappMessages } from "./whatsapp/messages";
import { SidebarPreferencesProvider } from "./sidebar-preferences";
import { ActivityProvider } from "./activity/ActivityIndicator";
import { activitySnapshot } from "./activity/model";
import { MailFolderDialog } from "./MailFolderDialog";
import { SharePointLibraryDialog } from "./SharePointLibraryDialog";

/**
 * The self-service WhatsApp surface (connect card + in-page wizard route) is
 * gated behind an explicit deployment flag. Until it is "true", no card and no
 * wizard route render, so the default behavior is identical to before — the
 * honesty rule (no dead card, no route to an unreleased bridge).
 *
 * A second, strictly e2e-only path enables the surface when the app runs under
 * the e2e auth bypass and the ?whatsapp=connect route is requested. This lets
 * the WhatsApp end-to-end spec exercise the flow without a global build flag
 * that would flip every other spec's honest flag-off grid. The e2e predicate is
 * false in production (DEV is false, MODE is not "e2e"), so it is never a
 * production backdoor.
 */
function whatsappSurfaceEnabled(): boolean {
  if (import.meta.env.VITE_OBSINA_WHATSAPP_ENABLED === "true") return true;
  const e2eBypass =
    import.meta.env.DEV &&
    import.meta.env.MODE === "e2e" &&
    import.meta.env.VITE_OBSINA_E2E_AUTH_BYPASS === "true";
  return (
    e2eBypass &&
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("whatsapp") === "connect"
  );
}

const emptyCoverage = {
  sources: { completed: 0, total: null, partial: 0, failed: 0, unsupported: 0 },
  attachments: {
    completed: 0,
    total: null,
    partial: 0,
    failed: 0,
    unsupported: 0,
  },
  text: { completed: 0, total: null, partial: 0, failed: 0, unsupported: 0 },
  graph: { completed: 0, total: null, partial: 0, failed: 0, unsupported: 0 },
  watermark: null,
  historyComplete: false,
};

const emptySummary: ControlPlaneSummary = parseControlPlaneSummary({
  tenantId: "20000000-0000-4000-8000-000000000099",
  state: "empty",
  tenantName: null,
  memberId: "30000000-0000-4000-8000-000000000099",
  memberName: null,
  connections: [],
  coverage: emptyCoverage,
  issues: [],
  updatedAt: new Date(0).toISOString(),
});

function defaultNavigate(url: string): void {
  window.location.assign(url);
}

function resourceCountIsConfigured(
  value: number | null | undefined,
): boolean | undefined {
  return value == null ? undefined : value > 0;
}

function useControlPlaneSummary(apiBase: string | null) {
  const preview = useMemo(
    () =>
      import.meta.env.DEV
        ? getPreviewSummary(
            new URLSearchParams(window.location.search).get("preview"),
          )
        : null,
    [],
  );
  const [summary, setSummary] = useState<ControlPlaneSummary | null>(
    preview ?? null,
  );
  const [loading, setLoading] = useState(!preview && Boolean(apiBase));
  const [error, setError] = useState<string | null>(null);
  const [checkedAt, setCheckedAt] = useState<Date | null>(
    preview ? new Date(preview.updatedAt) : null,
  );
  const [revision, setRevision] = useState(0);
  const reload = useCallback(() => setRevision((value) => value + 1), []);

  useEffect(() => {
    if (preview) return;
    if (!apiBase) {
      setSummary(emptySummary);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    getControlPlaneSummary(apiBase, controller.signal)
      .then((value) => {
        setSummary(value);
        setCheckedAt(new Date());
      })
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) {
          setError(
            reason instanceof Error
              ? reason.message
              : "Unknown control-plane error.",
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [apiBase, preview, revision]);

  // Live refresh in every state, not only while syncing. A stale
  // "failed"/"stopped"/"partial" summary otherwise never re-polls, so the UI
  // never learns the backend has resumed (scheduled sync, retry) and keeps
  // showing "Sync stopped" while the worker is actually running. Syncing polls
  // fast for live progress; other states poll slower to catch a backend
  // transition without hammering the API, and a hidden tab pauses either way.
  useEffect(() => {
    if (preview || !apiBase || loading) return;
    const intervalMs = summary?.state === "syncing" ? 5_000 : 25_000;
    const refresh = () => {
      if (document.visibilityState === "visible") reload();
    };
    const timeout = window.setTimeout(refresh, intervalMs);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearTimeout(timeout);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [apiBase, loading, preview, reload, summary?.state]);

  return {
    summary,
    initialLoading: loading && !summary,
    error,
    checkedAt,
    reload,
  };
}

export interface AuthenticatedAccount {
  readonly userId: string;
  readonly name: string;
  readonly email: string | null;
}

type PendingAction =
  "authorization" | "visibility" | "history" | `service:${string}`;

export interface AppProps {
  apiBase?: string | null;
  account?: AuthenticatedAccount | null;
  /**
   * Overrides the formatting locale. Injected only by tests that need to pin a
   * format; the application takes {@link APP_LOCALE} and never asks.
   */
  locale?: string;
  messages?: AppMessages;
  navigate?: (url: string) => void;
  onSignOut?: () => Promise<void> | void;
  /** Overrides the WhatsApp surface flag; defaults to the deployment env gate. */
  whatsappEnabled?: boolean;
  /** Initial wizard route, injected in tests; defaults to the ?whatsapp param. */
  initialConnecting?: "whatsapp" | null;
  /**
   * Reads the signed-in member's current access token. Supplied by the auth
   * root, which owns the Supabase client; the consent bridge is the one surface
   * that has to present that token to a host other than the API, and it gets it
   * from here rather than opening a second client of its own.
   */
  readAccessToken?: AccessTokenReader;
}

export function App({
  apiBase: apiBaseProp,
  account = null,
  locale = APP_LOCALE,
  messages = enMessages,
  navigate = defaultNavigate,
  onSignOut,
  readAccessToken,
  whatsappEnabled: whatsappEnabledProp,
  initialConnecting,
}: AppProps) {
  const apiBase =
    apiBaseProp === undefined
      ? normalizeApiBase(import.meta.env.VITE_OBSINA_API_BASE_URL)
      : apiBaseProp;
  const whatsappEnabled = whatsappEnabledProp ?? whatsappSurfaceEnabled();
  const { summary, initialLoading, error, checkedAt, reload } =
    useControlPlaneSummary(apiBase);
  // An assistant waiting on an approval outranks the default landing screen:
  // the person was sent here by it, mid-flow, and has one thing to decide.
  const [view, setView] = useState<AppView>(() =>
    readConsentRequest() ? "connect_assistant" : "control_plane",
  );
  const [connecting, setConnecting] = useState<"whatsapp" | null>(() => {
    if (!whatsappEnabled) return null;
    if (initialConnecting !== undefined) return initialConnecting;
    return typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("whatsapp") === "connect"
      ? "whatsapp"
      : null;
  });
  const [whatsappConnected, setWhatsappConnected] = useState(false);
  const whatsappTrigger = useRef<HTMLElement | null>(null);
  // Memoize the WhatsApp API client so the wizard's polling effect is not reset
  // by a fresh client object on every render.
  const whatsappApi = useMemo(
    () => (apiBase ? createWhatsAppApi(apiBase) : null),
    [apiBase],
  );

  const beginWhatsApp = () => {
    whatsappTrigger.current =
      typeof document !== "undefined" &&
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    setWhatsappConnected(false);
    setConnecting("whatsapp");
  };

  const restoreWhatsAppTrigger = (afterRestore?: () => void) => {
    const trigger = whatsappTrigger.current;
    whatsappTrigger.current = null;
    // The native dialog restores focus in browsers. This explicit fallback also
    // covers the test environment and any browser that removes modal focus late.
    globalThis.setTimeout(() => {
      if (trigger?.isConnected) trigger.focus();
      afterRestore?.();
    }, 0);
  };

  const cancelWhatsApp = () => {
    setConnecting(null);
    restoreWhatsAppTrigger();
  };

  const finishWhatsApp = () => {
    setConnecting(null);
    setWhatsappConnected(true);
    restoreWhatsAppTrigger(reload);
  };

  // The summary is already polled here for every view, so the shell-wide
  // activity sign is derived from it rather than fetching anything of its own.
  const activity = useMemo(
    () => activitySnapshot(summary, checkedAt),
    [checkedAt, summary],
  );

  return (
    <ObsinaProvider locale={locale} messages={APP_DESIGN_SYSTEM_MESSAGES}>
      <SidebarPreferencesProvider
        apiBase={apiBase}
        userId={account?.userId ?? null}
      >
        <ActivityProvider snapshot={activity}>
          {/* The commitments destination is closed for this phase: every row it
              draws is an extracted claim, and extraction is off, so the screen
              would be empty for as long as that holds. `OpenCommitmentsView` and
              its data path are left untouched — restoring the destination is
              swapping this branch back and putting the rail item back. */}
          {view === "commitments" ? (
            <PhaseClosedView
              account={account}
              activeView={view}
              body={messages.commitments.paused.body}
              messages={messages}
              onNavigate={setView}
              onSignOut={onSignOut}
              title={messages.commitments.paused.title}
            />
          ) : view === "memory_graph" ? (
            <MemoryGraphView
              account={account}
              activeView={view}
              apiBase={apiBase}
              messages={messages}
              onNavigate={setView}
              onSignOut={onSignOut}
            />
          ) : view === "sources" ? (
            <SourcesView
              account={account}
              activeView={view}
              apiBase={apiBase}
              messages={messages}
              onNavigate={setView}
              onSignOut={onSignOut}
            />
          ) : view === "connect_assistant" ? (
            <ConnectAssistantView
              account={account}
              activeView={view}
              messages={messages}
              onNavigate={setView}
              onSignOut={onSignOut}
              {...(readAccessToken ? { readAccessToken } : {})}
            />
          ) : view === "integrations" ? (
            <IntegrationsView
              account={account}
              activeView={view}
              apiBase={apiBase}
              messages={messages}
              onNavigate={setView}
              onSignOut={onSignOut}
            />
          ) : (
            <ControlPlaneView
              account={account}
              activeView={view}
              apiBase={apiBase}
              checkedAt={checkedAt}
              error={error}
              loading={initialLoading}
              messages={messages}
              navigate={navigate}
              onConnectWhatsApp={whatsappEnabled ? beginWhatsApp : undefined}
              onNavigate={setView}
              onReload={reload}
              onSignOut={onSignOut}
              summary={summary}
              whatsappEnabled={whatsappEnabled}
            />
          )}
        </ActivityProvider>
        {whatsappEnabled && whatsappApi && (
          <WhatsAppWizard
            api={whatsappApi}
            onCancel={cancelWhatsApp}
            onFinish={finishWhatsApp}
            open={connecting === "whatsapp"}
          />
        )}
        {whatsappConnected && (
          <ToastRegion
            className="action-toast-region"
            onDismiss={() => setWhatsappConnected(false)}
            toasts={[
              {
                id: "whatsapp-connected",
                title: whatsappMessages.connected.title,
                message: `${whatsappMessages.connected.connectedStatus} ${whatsappMessages.connected.privateStatus}`,
                tone: "success",
              },
            ]}
          />
        )}
      </SidebarPreferencesProvider>
    </ObsinaProvider>
  );
}

export interface ControlPlaneViewProps {
  account?: AuthenticatedAccount | null;
  apiBase: string | null;
  summary: ControlPlaneSummary | null;
  loading: boolean;
  checkedAt?: Date | null;
  error: string | null;
  messages?: AppMessages;
  navigate?: (url: string) => void;
  onReload?: () => void;
  onSignOut?: () => Promise<void> | void;
  /** Present only when the shared navigation is mounted (the full application). */
  activeView?: AppView;
  onNavigate?: (view: AppView) => void;
  /** Window used to open the authorization popup; injected only in tests. */
  authWindow?: Window;
  /** Whether the flagged WhatsApp connect card may render. */
  whatsappEnabled?: boolean;
  /** Opens the WhatsApp wizard; present only when the surface is enabled. */
  onConnectWhatsApp?: () => void;
}

export function ControlPlaneView({
  account = null,
  apiBase,
  summary,
  loading,
  checkedAt = null,
  error,
  messages = enMessages,
  navigate = defaultNavigate,
  onReload = () => undefined,
  onSignOut,
  activeView = "control_plane",
  onNavigate,
  authWindow = typeof window === "undefined" ? undefined : window,
  whatsappEnabled = false,
  onConnectWhatsApp,
}: ControlPlaneViewProps) {
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null,
  );
  const [signingOut, setSigningOut] = useState(false);

  // Contract 3 exposes a `connections` array. The per-connection detail surfaces
  // (scope, history, coverage, sync progress, reauthorization) manage the single
  // connection a tenant holds today; the grid iterates the array for the
  // overview.
  const connection = summary ? primaryConnection(summary) : null;

  const authorizeMicrosoft = (connectionId?: string) => {
    if (pendingAction) return;
    setActionError(null);
    setActionMessage(null);
    if (!apiBase) {
      setActionError(messages.errors.missingConfig);
      return;
    }
    const trigger =
      typeof document !== "undefined" &&
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    setPendingAction("authorization");
    void authorizeMicrosoftViaPopup({
      apiBase,
      connectionId,
      requestAuthorizationUrl: startMicrosoftAuthorization,
      fullPageNavigate: navigate,
      win: authWindow,
      // A cancellation is provisional: a COOP-severed popup handle reports
      // `closed` while the sign-in is still running. If authorization lands after
      // that, advance here rather than leaving the connection invisible until a
      // manual reload.
      onLateOutcome: (outcome) => {
        if (outcome.kind === "connected") onReload();
      },
    })
      .then((outcome) => {
        if (outcome.kind === "connected") {
          onReload();
        } else if (outcome.kind === "failed") {
          setActionError(messages.errors.actionFailed);
        }
        // "cancelled" returns to an idle state; "redirected" unloads the page.
      })
      .finally(() => {
        setPendingAction(null);
        if (trigger?.isConnected) trigger.focus();
      });
  };

  const beginMicrosoft = () => authorizeMicrosoft();

  const reauthorize = () => {
    setActionError(null);
    if (!apiBase || !connection) {
      setActionError(messages.errors.missingConfig);
      return;
    }
    authorizeMicrosoft(connection.id);
  };

  const perform = async (
    action: () => Promise<void>,
    kind: PendingAction,
    successMessage?: string,
    failureMessage?: (reason: unknown) => string,
  ) => {
    if (pendingAction) return;
    setPendingAction(kind);
    setActionError(null);
    setActionMessage(null);
    try {
      await action();
      if (successMessage) setActionMessage(successMessage);
      onReload();
    } catch (reason) {
      setActionError(failureMessage?.(reason) ?? messages.errors.actionFailed);
    } finally {
      setPendingAction(null);
    }
  };

  const chooseVisibility = (
    visibility: VisibilityClass,
    historyStartAt: string | null,
  ) => {
    if (!connection) return;
    if (summary?.synthetic) {
      setActionMessage(messages.syntheticBody);
      return;
    }
    if (!apiBase) {
      setActionError(messages.errors.missingConfig);
      return;
    }
    void perform(
      () => saveVisibility(apiBase, connection.id, visibility, historyStartAt),
      "visibility",
      undefined,
      (reason) =>
        isVisibilityTransitionResetRequired(reason)
          ? messages.errors.visibilityTransitionRequiresReset
          : messages.errors.actionFailed,
    );
  };

  const updateHistoryStart = (historyStartAt: string | null) => {
    if (!connection) return;
    if (summary?.synthetic) {
      setActionMessage(messages.syntheticBody);
      return;
    }
    if (!apiBase) {
      setActionError(messages.errors.missingConfig);
      return;
    }
    void perform(async () => {
      const result = await saveHistoryStart(
        apiBase,
        connection.id,
        historyStartAt,
        crypto.randomUUID(),
      );
      setActionMessage(
        result.status === "queued"
          ? messages.history.saved
          : messages.history.unchanged,
      );
    }, "history");
  };

  const toggleService = (source: SourceType, enabled: boolean) => {
    if (!connection) return;
    if (summary?.synthetic) {
      setActionMessage(messages.syntheticBody);
      return;
    }
    if (!apiBase) {
      setActionError(messages.errors.missingConfig);
      return;
    }
    const kind = source.kind;
    // Mail is the always-on coverage anchor and is not toggleable; a service
    // without a granted scope has no work to gate either.
    if (
      kind !== "calendar" &&
      kind !== "teams" &&
      kind !== "onedrive" &&
      kind !== "sharepoint"
    ) {
      return;
    }
    void perform(
      () => setServiceEnabled(apiBase, connection.id, kind, enabled),
      `service:${kind}`,
    );
  };

  const signOut = () => {
    if (!onSignOut || signingOut) return;
    setSigningOut(true);
    setActionError(null);
    void Promise.resolve(onSignOut())
      .catch(() => setActionError(messages.errors.signOutFailed))
      .finally(() => setSigningOut(false));
  };

  return (
    <AppShell
      account={account}
      activeView={activeView}
      messages={messages}
      onNavigate={onNavigate}
      onSignOut={onSignOut && signOut}
      overlays={
        <>
          <ToastRegion
            className="action-toast-region"
            onDismiss={() => setActionMessage(null)}
            toasts={
              actionMessage
                ? [
                    {
                      id: "action-complete",
                      title: messages.common.updateComplete,
                      message: actionMessage,
                      tone: "info",
                    },
                  ]
                : []
            }
          />
          {import.meta.env.DEV && <PreviewSwitcher label={messages.preview} />}
        </>
      }
      signingOut={signingOut}
      status={
        summary && summary.state !== "empty" ? (
          <ConnectionStatus messages={messages} state={summary.state} />
        ) : undefined
      }
    >
      {summary?.synthetic && (
        <Callout title={messages.syntheticTitle} tone="warning">
          {messages.syntheticBody}
        </Callout>
      )}
      {actionError && (
        <Callout announce title={messages.common.actionRequired} tone="danger">
          {actionError}
        </Callout>
      )}
      {error && summary && (
        <Callout announce title={messages.errors.staleTitle} tone="warning">
          {messages.errors.staleBody}
        </Callout>
      )}
      {error && !summary && (
        <Unavailable messages={messages} onReload={onReload} />
      )}
      {loading && <LoadingState label={messages.common.loading} />}
      {!loading && summary && (
        <SyncControlSurface
          apiBase={apiBase}
          checkedAt={checkedAt}
          messages={messages}
          onConnect={beginMicrosoft}
          onConnectWhatsApp={onConnectWhatsApp}
          onContinueScope={chooseVisibility}
          onHistoryStartChange={updateHistoryStart}
          onReauthorize={reauthorize}
          onReload={onReload}
          onToggleService={toggleService}
          pendingAction={pendingAction}
          summary={summary}
          visibilityActionError={actionError}
          whatsappEnabled={whatsappEnabled}
        />
      )}
    </AppShell>
  );
}

interface ConnectionStatusView {
  status: "synced" | "syncing" | "error" | "paused" | "offline";
  label: string;
}

function connectionStatusView(
  state: ControlPlaneState,
  messages: AppMessages,
): ConnectionStatusView {
  const map: Record<ControlPlaneState, ConnectionStatusView> = {
    empty: { status: "offline", label: messages.status.notConnected },
    scope_required: { status: "paused", label: messages.status.scopeRequired },
    syncing: { status: "syncing", label: messages.status.syncing },
    healthy: { status: "synced", label: messages.status.connected },
    partial: { status: "error", label: messages.status.partial },
    failed: { status: "error", label: messages.status.failed },
    reauthorization_required: {
      status: "error",
      label: messages.status.reconnect,
    },
  };
  return map[state];
}

/**
 * Every pill stays silent. The narrow layout hides the header pill, so a single
 * visually hidden live region in the shell owns the announcement instead; that
 * keeps exactly one announcement per state change at any viewport.
 */
function ConnectionStatus({
  state,
  messages,
}: {
  state: ControlPlaneState;
  messages: AppMessages;
}) {
  const value = connectionStatusView(state, messages);
  return (
    <StatusPill
      aria-busy={state === "syncing" || undefined}
      className="connection-status"
      label={value.label}
      status={value.status}
    />
  );
}

interface SyncControlSurfaceProps {
  apiBase: string | null;
  checkedAt: Date | null;
  messages: AppMessages;
  onConnect: () => void;
  onConnectWhatsApp?: () => void;
  onContinueScope: (
    value: VisibilityClass,
    historyStartAt: string | null,
  ) => void;
  onHistoryStartChange: (historyStartAt: string | null) => void;
  onReauthorize: () => void;
  onReload: () => void;
  onToggleService: (source: SourceType, enabled: boolean) => void;
  pendingAction: PendingAction | null;
  summary: ControlPlaneSummary;
  visibilityActionError: string | null;
  whatsappEnabled: boolean;
}

/**
 * The frame B control-plane surface. The grid is the source surface: it renders
 * real connections, working connect entries, and an explicitly non-interactive
 * Gmail roadmap card. Calendar / Teams appear only when the contract reports
 * them; WhatsApp appears only when linked or when its self-service flag is on.
 * Per-connection detail remains below the grid.
 */
function SyncControlSurface({
  apiBase,
  checkedAt,
  messages,
  onConnect,
  onConnectWhatsApp,
  onContinueScope,
  onHistoryStartChange,
  onReauthorize,
  onReload,
  onToggleService,
  pendingAction,
  summary,
  visibilityActionError,
  whatsappEnabled,
}: SyncControlSurfaceProps) {
  const connection = primaryConnection(summary);
  const isEmpty = summary.state === "empty";
  const showConnectCard = !summary.connections.some(
    (item) => item.provider === "microsoft",
  );
  // WhatsApp connections, split by what they actually are. Linked ones are
  // drawn as connected cards on their own honest terms (no Microsoft aggregate
  // state, no per-source coverage). A connection whose device never linked gets
  // the resume card instead: it is a recorded consent, and drawing it as
  // connected is what left the member with a card that said "waiting for first
  // sync" and no way to reach the wizard. Either one replaces the connect card,
  // so the grid never offers to start a second link over an existing one.
  const whatsapp = whatsappConnections(summary);
  const linkedWhatsapp = whatsapp.filter(
    (item) => whatsappLinkState(item) === "linked",
  );
  const unlinkedWhatsapp = whatsapp.filter(
    (item) => whatsappLinkState(item) === "awaiting_device",
  );
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [connectionInfoOpen, setConnectionInfoOpen] = useState(false);
  const [sourceStatusKind, setSourceStatusKind] = useState<SourceKind | null>(
    null,
  );
  const [metricDetails, setMetricDetails] = useState<MetricKey | null>(null);
  const [processingDetailsOpen, setProcessingDetailsOpen] = useState(false);
  const [sharePointLibrariesOpen, setSharePointLibrariesOpen] = useState(false);
  const [mailFoldersOpen, setMailFoldersOpen] = useState(false);
  // How many folders this member added beyond the two the plan always reads.
  // `null` is "not established", never zero: the card must not claim a default
  // scope it has not read back from the service.
  const [mailFolderCount, setMailFolderCount] = useState<number | null>(null);
  const [sharePointLocalCount, setSharePointLocalCount] = useState<
    number | null
  >(null);
  const [sharePointFallback, setSharePointFallback] = useState<{
    state: "idle" | "loading" | "ready" | "error";
    count: number | null;
  }>({ state: "idle", count: null });

  // Compact service rows are derived only from contract 3 sourceTypes. Missing
  // contract data stays unknown; the UI never fills the five rows with guessed
  // authorization or a fabricated zero count.
  const derivedSourceTypes: readonly SourceType[] =
    connection?.sourceTypes ?? [];
  const oneDriveSource =
    derivedSourceTypes.find((source) => source.kind === "onedrive") ?? null;
  const sharePointSource =
    derivedSourceTypes.find((source) => source.kind === "sharepoint") ?? null;

  useEffect(() => {
    setSharePointLocalCount(null);
    setMailFolderCount(null);
  }, [connection?.id]);

  // Newer summaries carry SharePoint `configured` / `resourceCount` directly.
  // During a rolling contract deployment, fall back to the selection endpoint
  // rather than claiming that a granted scope means a library was selected.
  useEffect(() => {
    const summaryHasConfiguration =
      sharePointSource?.configured !== undefined ||
      sharePointSource?.resourceCount !== undefined;
    if (
      !connection ||
      !sharePointSource?.granted ||
      connection.visibilityClass === "tenant_shared" ||
      summaryHasConfiguration ||
      sharePointLocalCount !== null
    ) {
      setSharePointFallback({ state: "idle", count: null });
      return;
    }
    if (!apiBase) {
      setSharePointFallback({ state: "error", count: null });
      return;
    }
    const controller = new AbortController();
    setSharePointFallback({ state: "loading", count: null });
    getSharePointDriveSelections(apiBase, connection.id, controller.signal)
      .then((selections) => {
        if (!controller.signal.aborted) {
          setSharePointFallback({
            state: "ready",
            count: selections.length,
          });
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setSharePointFallback({ state: "error", count: null });
        }
      });
    return () => controller.abort();
  }, [
    apiBase,
    connection,
    sharePointLocalCount,
    sharePointSource?.configured,
    sharePointSource?.granted,
    sharePointSource?.resourceCount,
  ]);

  const recordSharePointSelectionCount = useCallback(
    (count: number, reloadSummary = false) => {
      setSharePointLocalCount(count);
      setSharePointFallback({ state: "ready", count });
      if (reloadSummary) onReload();
    },
    [onReload],
  );

  const recordMailFolderCount = useCallback(
    (count: number, reloadSummary = false) => {
      setMailFolderCount(count);
      if (reloadSummary) onReload();
    },
    [onReload],
  );

  const sharePointSelectionCount =
    sharePointLocalCount ??
    sharePointSource?.resourceCount ??
    sharePointFallback.count;
  const sharePointFallbackCount =
    sharePointFallback.state === "ready" ? sharePointFallback.count : null;
  const sharePointConfigured =
    resourceCountIsConfigured(sharePointLocalCount) ??
    resourceCountIsConfigured(sharePointSource?.resourceCount) ??
    sharePointSource?.configured ??
    resourceCountIsConfigured(sharePointFallbackCount) ??
    null;
  const sharePointConfiguration: SourceConfigurationDescriptor | undefined =
    !sharePointSource?.granted
      ? undefined
      : connection?.visibilityClass === "tenant_shared"
        ? {
            message: messages.sharePointLibraries.cardNeedsPrivate,
            state: "visibility_required",
          }
        : sharePointFallback.state === "loading"
          ? {
              message: messages.sharePointLibraries.cardChecking,
              state: "loading",
            }
          : sharePointFallback.state === "error"
            ? {
                message: messages.sharePointLibraries.cardUnavailable,
                state: "error",
              }
            : sharePointConfigured === false
              ? {
                  message: messages.sharePointLibraries.cardNeedsSelection,
                  state: "required",
                }
              : sharePointConfigured === true
                ? {
                    message:
                      sharePointSelectionCount === null
                        ? messages.sharePointLibraries.cardConfigured
                        : messages.sharePointLibraries.cardSelectedCount(
                            sharePointSelectionCount,
                          ),
                    state: "configured",
                  }
                : undefined;
  // Mail always reads the two anchor folders, so this line never says "nothing
  // is configured": it says what the scope is. Before the chooser has been
  // opened the count is unknown, and the honest statement is the default scope
  // the plan runs — which is exactly what the worker does with no selections.
  const mailSource =
    derivedSourceTypes.find((source) => source.kind === "mail") ?? null;
  const mailConfiguration: SourceConfigurationDescriptor | undefined =
    !mailSource?.granted
      ? undefined
      : mailFolderCount === null
        ? {
            message: messages.mailFolders.cardDefaultScope,
            state: "configured",
          }
        : mailFolderCount === 0
          ? {
              message: messages.mailFolders.cardDefaultScope,
              state: "configured",
            }
          : {
              message: messages.mailFolders.cardSelectedCount(mailFolderCount),
              state: "configured",
            };
  const oneDriveConfiguration: SourceConfigurationDescriptor | undefined =
    oneDriveSource?.granted && connection?.visibilityClass === "tenant_shared"
      ? {
          message: messages.oneDriveFiles.cardNeedsPrivate,
          state: "visibility_required",
        }
      : undefined;

  const microsoftSources = connection
    ? orderMicrosoftSources(derivedSourceTypes)
    : [];
  const accountCount = linkedConnections(summary).length;
  const serviceCount = connectedServiceCount(summary);
  const providerStatuses = [
    connection
      ? connectorStatusDescriptor(summary.state, messages).label
      : null,
    ...linkedWhatsapp.map((connected) =>
      connected.lastSuccessfulSyncAt
        ? messages.connectors.status.connected
        : messages.connectors.status.connectedWaiting,
    ),
    ...unlinkedWhatsapp.map(() => messages.connectors.status.linkIncomplete),
  ]
    .filter((status): status is string => status !== null)
    .join(". ");

  return (
    <div className="sync-control">
      <SyncControlHeader messages={messages} summary={summary} />
      {/* The self-updating surface no longer raises a standing attention
          callout. Any issue stays honestly preserved in the connection's
          "Items requiring attention" detail below; the top of the surface
          shows no user-triggering warning. */}
      {isEmpty && <FirstRunGuide messages={messages} />}
      {!sharePointLibrariesOpen && (
        <p aria-live="polite" className="obsina-sr-only" role="status">
          {messages.syncControl.liveSummary(
            accountCount,
            serviceCount,
            providerStatuses,
          )}
        </p>
      )}
      {summary.state === "scope_required" && (
        <ScopeDecision
          apiBase={apiBase}
          messages={messages}
          onContinue={onContinueScope}
          pending={pendingAction === "visibility"}
          summary={summary}
        />
      )}
      <section
        aria-labelledby="connected-accounts-heading"
        className="connector-section"
      >
        <h2 id="connected-accounts-heading">
          {messages.syncControl.connectionsHeading}
        </h2>
        <div className="connector-layout">
          <ul
            aria-label={messages.syncControl.connectedRegion}
            className="connector-provider-list"
          >
            {connection ? (
              <li className="connector-provider-list__microsoft">
                <Microsoft365ConnectorGroup
                  connection={connection}
                  messages={messages}
                  onExplainSourceStatus={setSourceStatusKind}
                  onReauthorize={onReauthorize}
                  onShowConnectionInfo={() => setConnectionInfoOpen(true)}
                  onShowProcessingDetails={() => setProcessingDetailsOpen(true)}
                  onShowSyncDetails={() => setDetailsOpen(true)}
                  onToggleService={onToggleService}
                  pendingAction={pendingAction}
                  sourceConfigurations={{
                    mail: mailConfiguration,
                    onedrive: oneDriveConfiguration,
                    sharepoint: sharePointConfiguration,
                  }}
                  sourceManagement={{
                    mail: mailSource?.granted
                      ? {
                          icon: FolderTree,
                          label: messages.mailFolders.manage,
                          onManage: () => setMailFoldersOpen(true),
                          variant: "ghost" as const,
                        }
                      : undefined,
                    onedrive: oneDriveConfiguration
                      ? {
                          icon: LockKeyhole,
                          label: messages.oneDriveFiles.makePrivate,
                          loadingLabel: messages.oneDriveFiles.makingPrivate,
                          onManage: () =>
                            onContinueScope(
                              "owner_private",
                              connection.historyStartAt,
                            ),
                        }
                      : undefined,
                    sharepoint: sharePointSource?.granted
                      ? {
                          icon: Library,
                          label: messages.sharePointLibraries.manage,
                          onManage: () => setSharePointLibrariesOpen(true),
                          variant: "ghost" as const,
                        }
                      : undefined,
                  }}
                  sources={microsoftSources}
                  summary={summary}
                />
              </li>
            ) : (
              showConnectCard && (
                <li className="connector-provider-list__microsoft">
                  <ConnectorConnectCard
                    messages={messages}
                    onConnect={onConnect}
                    pending={pendingAction === "authorization"}
                  />
                </li>
              )
            )}
          </ul>
          <ul
            aria-label={messages.syncControl.otherSourcesRegion}
            className="connector-rail"
          >
            {linkedWhatsapp.map((connected) => (
              <li key={connected.id}>
                <WhatsAppConnectorCard
                  connection={connected}
                  messages={messages}
                />
              </li>
            ))}
            {unlinkedWhatsapp.map((unlinked) => (
              <li key={unlinked.id}>
                <WhatsAppConnectCard
                  messages={messages}
                  state="awaiting_device"
                  {...(whatsappEnabled && onConnectWhatsApp
                    ? { onConnect: onConnectWhatsApp }
                    : {})}
                />
              </li>
            ))}
            {whatsappEnabled && onConnectWhatsApp && whatsapp.length === 0 && (
              <li>
                <WhatsAppConnectCard
                  messages={messages}
                  onConnect={onConnectWhatsApp}
                />
              </li>
            )}
            <li>
              <GmailPlannedCard messages={messages} />
            </li>
          </ul>
        </div>
        <p className="connector-section__note">
          {messages.syncControl.plannedNote}
        </p>
      </section>
      {!isEmpty && summary.state !== "scope_required" && (
        <details className="sync-overview-details">
          <summary>
            <span className="sync-overview-details__copy">
              <strong>{messages.syncControl.detailsTitle}</strong>
              <span>{messages.syncControl.detailsBody}</span>
            </span>
            <Icon
              aria-hidden="true"
              className="sync-overview-details__chevron"
              icon={ChevronDown}
              size={18}
            />
          </summary>
          <div className="sync-overview-details__body">
            <ConnectedDetail
              apiBase={apiBase}
              messages={messages}
              metricDetails={metricDetails}
              onHistoryStartChange={onHistoryStartChange}
              onShowMetricDetails={setMetricDetails}
              pendingAction={pendingAction}
              summary={summary}
            />
          </div>
        </details>
      )}
      <SyncDetailsDialog
        asOf={checkedAt ?? new Date(summary.updatedAt)}
        messages={messages}
        onOpenChange={setDetailsOpen}
        open={detailsOpen}
        summary={summary}
      />
      {connection && (
        <ConnectionStatusDialog
          checkedAt={checkedAt}
          connection={connection}
          messages={messages}
          onOpenChange={setConnectionInfoOpen}
          open={connectionInfoOpen}
          summary={summary}
        />
      )}
      <ProcessingDetailDialog
        connection={connection}
        messages={messages}
        onOpenChange={setProcessingDetailsOpen}
        open={processingDetailsOpen}
        summary={summary}
      />
      <SourceStatusDialog
        configuration={
          sourceStatusKind === "onedrive"
            ? oneDriveConfiguration
            : sourceStatusKind === "sharepoint"
              ? sharePointConfiguration
              : undefined
        }
        messages={messages}
        onOpenChange={(open) => {
          if (!open) setSourceStatusKind(null);
        }}
        open={sourceStatusKind !== null}
        sourceKind={sourceStatusKind}
      />
      <CoverageMetricDialog
        messages={messages}
        metricKey={metricDetails}
        onOpenChange={(open) => {
          if (!open) setMetricDetails(null);
        }}
        open={metricDetails !== null}
        summary={summary}
      />
      {connection && (
        <MailFolderDialog
          apiBase={apiBase}
          connectionId={connection.id}
          messages={messages}
          onOpenChange={setMailFoldersOpen}
          onSelectionChange={recordMailFolderCount}
          open={mailFoldersOpen}
        />
      )}
      {connection && (
        <SharePointLibraryDialog
          apiBase={apiBase}
          connectionId={connection.id}
          connectionVisibility={connection.visibilityClass}
          messages={messages}
          onOpenChange={setSharePointLibrariesOpen}
          onRequestPrivate={() =>
            onContinueScope("owner_private", connection.historyStartAt)
          }
          onSelectionChange={recordSharePointSelectionCount}
          open={sharePointLibrariesOpen}
          privateRequestError={visibilityActionError}
          privateRequestPending={pendingAction === "visibility"}
        />
      )}
    </div>
  );
}

const sourceKindIcon: Record<SourceKind, LucideIcon> = {
  mail: Mail,
  calendar: Calendar,
  teams: MessagesSquare,
  onedrive: Cloud,
  sharepoint: Library,
};

const microsoftSourceOrder: readonly SourceKind[] = [
  "mail",
  "calendar",
  "teams",
  "onedrive",
  "sharepoint",
];

function orderMicrosoftSources(
  sources: readonly SourceType[],
): readonly SourceType[] {
  const byKind = new Map(sources.map((source) => [source.kind, source]));
  return microsoftSourceOrder.flatMap((kind) => {
    const source = byKind.get(kind);
    return source ? [source] : [];
  });
}

/**
 * The connections the account and service totals may speak for. A WhatsApp
 * connection whose device was never linked is a recorded consent, not a
 * connected account: counting it would have the header claim an account while
 * its own card says the link is unfinished and nothing is being read.
 */
function linkedConnections(
  summary: ControlPlaneSummary,
): readonly ConnectionSummary[] {
  return summary.connections.filter(
    (connection) =>
      connection.provider !== "whatsapp" ||
      whatsappLinkState(connection) === "linked",
  );
}

/**
 * Counts only explicit provider truth. A Microsoft connection without contract
 * 3 sourceTypes makes the aggregate unknown rather than silently turning that
 * account into zero connected services. WhatsApp is one service per linked
 * account because the connection itself is the provider's source boundary.
 */
function connectedServiceCount(summary: ControlPlaneSummary): number | null {
  let count = 0;
  for (const connection of linkedConnections(summary)) {
    if (connection.provider === "microsoft") {
      if (!connection.sourceTypes) return null;
      count += connection.sourceTypes.filter((source) => source.granted).length;
      continue;
    }
    if (connection.provider === "whatsapp") {
      count += 1;
      continue;
    }
    return null;
  }
  return count;
}

/**
 * Genuine text/evidence gaps the reconciled cut-off must not hide: cumulative
 * all-time items that failed or came through partial. Once the state machine
 * reports `healthy`, the run has reconciled, yet these gaps can persist in the
 * all-time view; they are counted honestly beside the reconciled line rather
 * than retracting it. `unsupported` is deliberately excluded — it is a
 * design-expected terminal outcome that reconciliation already tolerates, so
 * counting it would brand every healthy connection with a standing caveat. The
 * coverage tiles still surface the unsupported counts per metric.
 */
function terminalResidueCount(coverage: Coverage): number {
  const residueOf = (metric: CoverageMetric): number =>
    metric.partial + metric.failed;
  return residueOf(coverage.text) + residueOf(coverage.graph);
}

function SyncControlHeader({
  messages,
  summary,
}: {
  messages: AppMessages;
  summary: ControlPlaneSummary;
}) {
  const { locale } = useObsinaI18n();
  const accountCount = linkedConnections(summary).length;
  const serviceCount = connectedServiceCount(summary);
  const microsoft = primaryConnection(summary);
  const hasMicrosoft = microsoft !== null;
  const watermark = summary.coverage.watermark;
  // Bind the reconciled claim to the state machine's authoritative decision —
  // the same signal that paints the status pill — not to the cumulative
  // coverage view sent to the client. That view is all-time, so a single
  // persistent terminal failed/partial item would keep it "unreconciled"
  // forever while the pill reads "Up to date", contradicting itself on one
  // screen. A non-null watermark alone (e.g. mid-sync) is still only a progress
  // marker, surfaced as an honest "data through" cut-off.
  const reconciled = summary.state === "healthy";
  // No watermark is not one state but two, and neither of them is "catching
  // up". Measured on the live tenant: every one of the 132 failed runs carried
  // a null `source_watermark` and every one of the 1758 that completed carried
  // a real one, so a null means the run produced no cut-off — never that one is
  // being approached. When the connection has never been attempted that is
  // "not started yet"; when it has been attempted and still has no cut-off
  // anywhere, the honest answer is that none is established.
  const attempted = microsoft?.lastAttemptAt != null;
  const asOfText = !hasMicrosoft
    ? null
    : reconciled && watermark
      ? messages.syncControl.reconciledUpTo(formatDate(watermark, locale, ""))
      : watermark
        ? messages.syncControl.coveredThrough(formatDate(watermark, locale, ""))
        : attempted
          ? messages.syncControl.coverageNotEstablished
          : messages.syncControl.syncNotStarted;
  // Honesty is preserved, not over-claimed: when the reconciled line is shown
  // yet the cumulative coverage still holds terminal-accepted text/evidence
  // residue, an explicit count sits beside it. It never fires while the surface
  // is genuinely mid-sync (reconciled is false there), so the copy never drifts
  // from over-honest to under-honest.
  const residue = terminalResidueCount(summary.coverage);
  const residueNote =
    hasMicrosoft && reconciled && residue > 0
      ? messages.syncControl.reconciledResidue(residue)
      : null;
  return (
    <div className="sync-control__header">
      <div>
        <div className="obsina-eyebrow">{messages.syncControl.eyebrow}</div>
        <h1>{messages.syncControl.title}</h1>
      </div>
      <p className="sync-control__asof">
        <Icon icon={Clock3} size={15} />
        <span>
          {messages.syncControl.connectionSummary(accountCount, serviceCount)}
          {asOfText && (
            <>
              {messages.syncControl.asOfSeparator}
              {asOfText}
            </>
          )}
          {residueNote && (
            <>
              {messages.syncControl.asOfSeparator}
              <span className="sync-control__residue">{residueNote}</span>
            </>
          )}
        </span>
      </p>
    </div>
  );
}

function FirstRunGuide({ messages }: { messages: AppMessages }) {
  const steps = [
    [Link2, messages.syncControl.firstRun.stepConnect],
    [ShieldCheck, messages.syncControl.firstRun.stepScope],
    [Network, messages.syncControl.firstRun.stepBuild],
  ] as const;
  return (
    <section className="first-run">
      <span aria-hidden="true" className="first-run__emblem">
        <Icon icon={Network} size={26} />
      </span>
      <div className="first-run__copy">
        <h2>{messages.syncControl.firstRun.title}</h2>
        <p>{messages.syncControl.firstRun.body}</p>
      </div>
      <ol className="first-run__steps">
        {steps.map(([icon, label]) => (
          <li key={label}>
            <Icon icon={icon} size={14} />
            {label}
          </li>
        ))}
      </ol>
    </section>
  );
}

function ConnectorGridIcon({ tone }: { tone: "brand" | "ghost" }) {
  return (
    <span className="connector-card__icon" data-tone={tone}>
      <Icon icon={Cloud} size={18} />
    </span>
  );
}

interface ConnectorStatusDescriptor {
  status: StatusKind;
  icon: LucideIcon;
  label: string;
  tone: "success" | "warning" | "danger" | "info" | "neutral";
}

function connectorStatusDescriptor(
  state: ControlPlaneState,
  messages: AppMessages,
): ConnectorStatusDescriptor {
  const status = messages.connectors.status;
  switch (state) {
    case "healthy":
      return {
        status: "healthy",
        icon: Check,
        label: status.healthy,
        tone: "success",
      };
    case "syncing":
      return {
        status: "syncing",
        icon: RefreshCw,
        label: status.syncing,
        tone: "warning",
      };
    case "partial":
      return {
        status: "partial",
        icon: AlertTriangle,
        label: status.partial,
        tone: "warning",
      };
    case "failed":
      return {
        status: "error",
        icon: AlertTriangle,
        label: status.failed,
        tone: "danger",
      };
    case "reauthorization_required":
      return {
        status: "reauth",
        icon: KeyRound,
        label: status.reauth,
        tone: "danger",
      };
    case "scope_required":
      return {
        status: "paused",
        icon: ShieldCheck,
        label: status.scopePending,
        tone: "neutral",
      };
    default:
      return {
        status: "disconnected",
        icon: Unplug,
        label: status.disconnected,
        tone: "neutral",
      };
  }
}

function ConnectorConnectCard({
  messages,
  onConnect,
  pending,
}: {
  messages: AppMessages;
  onConnect: () => void;
  pending: boolean;
}) {
  const provider = messages.connectors.provider.microsoft;
  return (
    <Card
      aria-label={messages.connectors.cardLabel(provider)}
      as="section"
      className="connector-card connector-card--empty provider-card provider-card--connect"
    >
      <div className="connector-card__head">
        <ConnectorGridIcon tone="ghost" />
        <div className="connector-card__titles">
          <div className="connector-card__name">{provider}</div>
          <div className="connector-card__src">
            {messages.connectors.microsoftServicesList}
          </div>
        </div>
      </div>
      <div className="connector-card__pill">
        <StatusPill
          icon={Unplug}
          label={messages.connectors.status.disconnected}
          status="disconnected"
        />
      </div>
      <p className="connector-card__empty-body">
        {messages.connectors.connect.mailBody}
      </p>
      <div className="connector-card__actions">
        <Button
          iconStart={<Icon icon={Plus} size={16} />}
          loading={pending}
          onClick={onConnect}
          size="sm"
        >
          {messages.connectors.connect.cta(provider)}
        </Button>
      </div>
    </Card>
  );
}

/**
 * An honest product-roadmap card. Google sign-in currently proves identity only;
 * it does not grant mailbox access or produce a source connection. Until that
 * backend slice exists, Gmail stays visible without a dead or misleading CTA.
 */
function GmailPlannedCard({ messages }: { messages: AppMessages }) {
  const provider = messages.connectors.provider.google;
  return (
    <Card
      aria-label={messages.connectors.cardLabel(
        messages.connectors.connect.gmailTitle,
        provider,
      )}
      as="section"
      className="connector-card connector-card--empty connector-card--planned provider-card provider-card--planned"
      data-availability="planned"
    >
      <div className="connector-card__head">
        <span className="connector-card__icon" data-tone="ghost">
          <Icon icon={Mail} size={18} />
        </span>
        <div className="connector-card__titles">
          <div className="connector-card__name">
            {messages.connectors.connect.gmailTitle}
          </div>
          <div className="connector-card__src">{provider}</div>
        </div>
      </div>
      <div className="connector-card__pill">
        <StatusPill
          icon={Clock3}
          label={messages.connectors.status.planned}
          status="offline"
        />
      </div>
      <p className="connector-card__empty-body">
        {messages.connectors.connect.gmailBody}
      </p>
    </Card>
  );
}

/**
 * The WhatsApp entry card, in its two honest shapes.
 *
 * `absent` is the flagged connect entry: no connection exists, so it mirrors the
 * Mail connect card and claims neither a connection nor coverage. `awaiting_device`
 * is a connection whose device never linked — a recorded consent and nothing
 * more. Both are the same card because they are the same decision: one press
 * into the wizard, which records consent under the current notice and pairs.
 *
 * The action is optional, and only for the flag. A member whose link is
 * unfinished must still be told so when the self-service flag is off; what the
 * flag gates is the wizard, not the truth about their connection.
 */
function WhatsAppConnectCard({
  messages,
  onConnect,
  state = "absent",
}: {
  messages: AppMessages;
  onConnect?: () => void;
  state?: WhatsAppLinkState | "absent";
}) {
  const awaiting = state === "awaiting_device";
  return (
    <Card
      aria-label={messages.connectors.cardLabel(
        messages.connectors.sourceType.whatsapp,
      )}
      as="section"
      className="connector-card connector-card--empty provider-card provider-card--whatsapp"
    >
      <div className="connector-card__head">
        <span className="connector-card__icon" data-tone="ghost">
          <Icon icon={MessageCircle} size={18} />
        </span>
        <div className="connector-card__titles">
          <div className="connector-card__name">
            {messages.connectors.connect.whatsappTitle}
          </div>
          <div className="connector-card__src">
            {messages.connectors.provider.whatsapp}
          </div>
        </div>
      </div>
      <div className="connector-card__pill">
        <StatusPill
          icon={awaiting ? Smartphone : Unplug}
          label={
            awaiting
              ? messages.connectors.status.linkIncomplete
              : messages.connectors.status.disconnected
          }
          status={awaiting ? "paused" : "disconnected"}
        />
      </div>
      <p className="connector-card__empty-body">
        {awaiting
          ? messages.connectors.connect.whatsappResumeBody
          : messages.connectors.connect.whatsappBody}
      </p>
      {onConnect && (
        <div className="connector-card__actions">
          <Button
            iconStart={<Icon icon={awaiting ? Link2 : Plus} size={16} />}
            onClick={onConnect}
            size="sm"
          >
            {awaiting
              ? messages.connectors.connect.whatsappResumeCta
              : messages.connectors.sourceCard.connectCta(
                  messages.connectors.sourceType.whatsapp,
                )}
          </Button>
        </div>
      )}
    </Card>
  );
}

/**
 * A linked WhatsApp connection as an overview card. WhatsApp has no per-source
 * coverage read surface yet, so the card never borrows the Microsoft aggregate
 * state or watermark and never claims per-source counts. Its status is derived
 * client-side from the connection's own honest fields. A linked connection with
 * no successful sync is "waiting for first sync", not indefinitely "Syncing";
 * once a sync lands it shows the connection's last successful sync time.
 */
function WhatsAppConnectorCard({
  connection,
  messages,
}: {
  connection: ConnectionSummary;
  messages: AppMessages;
}) {
  const { locale } = useObsinaI18n();
  const label = messages.connectors.sourceType.whatsapp;
  const synced = connection.lastSuccessfulSyncAt;
  const descriptor: ConnectorStatusDescriptor = synced
    ? {
        status: "healthy",
        icon: Check,
        label: messages.connectors.status.connected,
        tone: "success",
      }
    : {
        status: "paused",
        icon: Clock3,
        label: messages.connectors.status.connectedWaiting,
        tone: "neutral",
      };
  const asOf = synced
    ? messages.connectors.lastSuccess(formatDate(synced, locale, ""))
    : messages.connectors.sourceCard.coveragePending;
  return (
    <Card
      aria-label={messages.connectors.cardLabel(label)}
      as="section"
      className="connector-card provider-card provider-card--whatsapp"
      data-status={descriptor.status}
      data-type="whatsapp"
    >
      <div className="connector-card__head">
        <span className="connector-card__icon" data-tone="brand">
          <Icon icon={MessageCircle} size={18} />
        </span>
        <div className="connector-card__titles">
          <div className="connector-card__name">{label}</div>
          <div className="connector-card__src">{connection.accountLabel}</div>
        </div>
        {connection.visibilityClass !== null && (
          <ConnectionVisibilityBadge
            connection={connection}
            messages={messages}
          />
        )}
      </div>
      <div className="connector-card__pill">
        <StatusPill
          icon={descriptor.icon}
          label={descriptor.label}
          status={descriptor.status}
        />
      </div>
      <p className="connector-card__asof-line">
        <Icon icon={Clock3} size={14} />
        {asOf}
      </p>
    </Card>
  );
}

const healthIconByState = {
  syncing: RefreshCw,
  healthy: ShieldCheck,
  partial: AlertTriangle,
  failed: AlertTriangle,
  reauthorization_required: KeyRound,
} as const;

function connectorHealthCopy(
  summary: ControlPlaneSummary,
  messages: AppMessages,
): readonly [string, string, "info" | "success" | "warning" | "danger"] | null {
  const syncingHealth: readonly [string, string, "info"] =
    summary.syncProgress?.inventoryMode === "initial"
      ? [messages.health.syncingTitle, messages.health.syncingBody, "info"]
      : summary.syncProgress?.inventoryMode === "incremental"
        ? [
            messages.health.syncingIncrementalTitle,
            messages.health.syncingIncrementalBody,
            "info",
          ]
        : [
            messages.health.syncingStartingTitle,
            messages.health.syncingStartingBody,
            "info",
          ];
  const healthByState = {
    syncing: syncingHealth,
    healthy: [
      messages.health.healthyTitle,
      messages.health.healthyBody,
      "success",
    ],
    partial: [
      messages.health.partialTitle,
      messages.health.partialBody,
      "warning",
    ],
    failed: [messages.health.failedTitle, messages.health.failedBody, "danger"],
    reauthorization_required: [
      messages.health.reauthTitle,
      messages.health.reauthBody,
      "danger",
    ],
  } as const;
  return healthByState[summary.state as keyof typeof healthByState] ?? null;
}

function ConnectionVisibilityBadge({
  connection,
  messages,
}: {
  connection: ConnectionSummary;
  messages: AppMessages;
}) {
  return (
    <Badge
      tone={
        connection.visibilityClass === "tenant_shared" ? "accent" : "neutral"
      }
    >
      <Icon
        icon={
          connection.visibilityClass === "tenant_shared"
            ? Building2
            : connection.visibilityClass === "owner_private"
              ? LockKeyhole
              : ShieldCheck
        }
        size={12}
      />
      {connection.visibilityClass === "tenant_shared"
        ? messages.connectors.visibilityShared
        : connection.visibilityClass === "owner_private"
          ? messages.connectors.visibilityPrivate
          : messages.connectors.visibilityPending}
    </Badge>
  );
}

interface SourceConfigurationDescriptor {
  readonly message: string;
  readonly state:
    "loading" | "error" | "required" | "visibility_required" | "configured";
}

interface SourceManagementDescriptor {
  readonly icon: LucideIcon;
  readonly label: string;
  readonly loadingLabel?: string;
  readonly onManage: () => void;
  /** Routine configuration stays quiet; a state fix keeps the stronger button. */
  readonly variant?: "secondary" | "ghost";
}

interface MicrosoftSourceConfigurations {
  readonly mail: SourceConfigurationDescriptor | undefined;
  readonly onedrive: SourceConfigurationDescriptor | undefined;
  readonly sharepoint: SourceConfigurationDescriptor | undefined;
}

interface MicrosoftSourceManagement {
  readonly mail: SourceManagementDescriptor | undefined;
  readonly onedrive: SourceManagementDescriptor | undefined;
  readonly sharepoint: SourceManagementDescriptor | undefined;
}

function sourceConfigurationIcon(
  configuration: SourceConfigurationDescriptor,
): LucideIcon {
  switch (configuration.state) {
    case "loading":
      return RefreshCw;
    case "error":
      return AlertTriangle;
    case "required":
      return FolderOpen;
    case "visibility_required":
      return LockKeyhole;
    default:
      return Check;
  }
}

function microsoftSourceStatusDescriptor(
  source: SourceType,
  configuration: SourceConfigurationDescriptor | undefined,
  summary: ControlPlaneSummary,
  messages: AppMessages,
): ConnectorStatusDescriptor {
  if (!source.granted) {
    return {
      status: "disconnected",
      icon: Unplug,
      label: messages.connectors.status.disconnected,
      tone: "neutral",
    };
  }
  if (summary.state === "reauthorization_required") {
    return connectorStatusDescriptor(summary.state, messages);
  }
  if (
    configuration?.state === "required" ||
    configuration?.state === "visibility_required"
  ) {
    return {
      status: "paused",
      icon:
        configuration.state === "visibility_required"
          ? LockKeyhole
          : FolderOpen,
      label:
        configuration.state === "visibility_required"
          ? messages.connectors.status.visibilityRequired
          : messages.connectors.status.configurationRequired,
      tone: "warning",
    };
  }
  if (configuration?.state === "error") {
    return {
      ...connectorStatusDescriptor("partial", messages),
      label: messages.connectors.status.coverageIncomplete,
    };
  }
  if (source.watermark === null && summary.state === "healthy") {
    return {
      status: "paused",
      icon: Clock3,
      label: messages.connectors.sourceCard.connectedCoveragePending,
      tone: "neutral",
    };
  }
  if (source.watermark === null && summary.state === "partial") {
    return {
      ...connectorStatusDescriptor("partial", messages),
      label: messages.connectors.status.coverageIncomplete,
    };
  }
  return connectorStatusDescriptor(summary.state, messages);
}

/**
 * The live queue depth for one service, drawn where the work happens. Words
 * carry every state, so a failed count never depends on colour alone. Counts
 * are rendered only when the connection actually reported them and something is
 * in flight: an absent field stays silent rather than claiming an empty queue.
 */
function ProcessingCountLine({
  counts,
  labelled = false,
  showFailed = false,
  messages,
}: {
  counts: SourceProcessing;
  /** Draws the "Processing" lead-in visibly instead of for assistive tech only. */
  labelled?: boolean;
  /**
   * Whether to state the failed count. The default self-updating surface keeps
   * failed jobs off the row, pill and footer — the worker retries them on its
   * own — so only the processing detail dialog opts in to show the honest
   * failed detail.
   */
  showFailed?: boolean;
  messages: AppMessages;
}) {
  const copy = messages.processing;
  return (
    <p className="processing-counts">
      <span
        className={labelled ? "processing-counts__label" : "obsina-sr-only"}
      >
        {copy.title}
      </span>
      {counts.queued > 0 && (
        <span>
          <Icon icon={Clock3} size={13} />
          {copy.stageQueued(counts.queued)}
        </span>
      )}
      {counts.inProgress > 0 && (
        <span>
          <Icon icon={RefreshCw} size={13} />
          {copy.stageInFlight(counts.inProgress)}
        </span>
      )}
      {showFailed && counts.failed > 0 && (
        <span data-tone="danger">
          <Icon icon={AlertTriangle} size={13} />
          {copy.stageFailed(counts.failed)}
        </span>
      )}
    </p>
  );
}

/**
 * A configuration state the row states inline. `required` and
 * `visibility_required` are already named by the status pill, so their sentence
 * moves behind an info control instead of a standing warning panel.
 */
function isExplainedConfiguration(
  configuration: SourceConfigurationDescriptor | undefined,
): boolean {
  return (
    configuration?.state === "required" ||
    configuration?.state === "visibility_required"
  );
}

/**
 * One honest Microsoft service row. Identity and privacy live at provider level;
 * the row carries only its own authorization, watermark and resource choice.
 * Coverage reads on the same line as the status pill, and a watermark every
 * granted service is missing is stated once by the list rather than per row.
 */
function MicrosoftSourceRow({
  configuration,
  connection,
  coveragePendingHoisted,
  frozenHoisted,
  management,
  messages,
  onExplainStatus,
  onToggleService,
  pendingAction,
  source,
  summary,
}: {
  configuration?: SourceConfigurationDescriptor;
  connection: ConnectionSummary;
  coveragePendingHoisted: boolean;
  frozenHoisted: boolean;
  management?: SourceManagementDescriptor;
  messages: AppMessages;
  onExplainStatus: () => void;
  onToggleService: (source: SourceType, enabled: boolean) => void;
  pendingAction: PendingAction | null;
  source: SourceType;
  summary: ControlPlaneSummary;
}) {
  const { locale } = useObsinaI18n();
  const label = messages.connectors.sourceType[source.kind];
  const card = messages.connectors.sourceCard;
  const isReauth =
    source.granted && summary.state === "reauthorization_required";
  const descriptor = microsoftSourceStatusDescriptor(
    source,
    configuration,
    summary,
    messages,
  );
  // Same authoritative binding as the header and the status pill: a granted
  // service reads "Reconciled up to" only when the connection state has actually
  // reconciled. Binding to the cumulative coverage view instead would let a
  // persistent all-time terminal residue contradict a healthy pill with a
  // per-row "Coverage through" on this same screen. The row still states its own
  // per-service watermark, so the cut-off stays honest per service.
  const reconciled = summary.state === "healthy";
  const asOf =
    reconciled && source.watermark
      ? card.coverageReconciled(formatDate(source.watermark, locale, ""))
      : source.watermark
        ? card.coverageThrough(formatDate(source.watermark, locale, ""))
        : coveragePendingHoisted
          ? null
          : card.coveragePending;
  const canManage = source.granted && !isReauth && management;
  // The four optional services carry an owner switch; Mail is the always-on
  // coverage anchor and never shows one. The toggle appears only when the read
  // surface actually reports the flag (contract 3), so an older payload without
  // it stays unchanged. `enabled` is independent of `granted`.
  const toggleable =
    source.granted &&
    !isReauth &&
    source.enabled !== undefined &&
    (source.kind === "calendar" ||
      source.kind === "teams" ||
      source.kind === "onedrive" ||
      source.kind === "sharepoint");
  const servicePending = pendingAction === `service:${source.kind}`;
  const explained =
    source.granted && !isReauth && isExplainedConfiguration(configuration);
  const inlineConfiguration =
    configuration && source.granted && !isReauth && !explained
      ? configuration
      : null;
  // Per-source queue depth, when the connection reported it. A read surface
  // that returned zeros and one that returned nothing both leave the row quiet;
  // the list footer is where those two stay apart in words.
  const processing = source.processing;
  const liveProcessing =
    processing && hasLiveWork(processing) ? processing : null;
  // Queued plus in progress is the service's live work; failed jobs are handled
  // out of band and never count as "moving now" on the pill.
  const activeWork = liveProcessing
    ? liveProcessing.queued + liveProcessing.inProgress
    : 0;
  return (
    <section
      aria-label={messages.connectors.cardLabel(label)}
      aria-busy={configuration?.state === "loading" ? true : undefined}
      className="microsoft-source-row"
      data-status={descriptor.status}
      data-type={source.kind}
    >
      <div className="microsoft-source-row__identity">
        <span
          className="connector-card__icon"
          data-tone={source.granted ? "brand" : "ghost"}
        >
          <Icon icon={sourceKindIcon[source.kind]} size={17} />
        </span>
        {/* The provider head above already names Microsoft 365; repeating it on
            every service row only cost height and got ellipsised to "Micr..." in
            the card's narrow first track. */}
        <div className="connector-card__titles">
          <div className="connector-card__name">{label}</div>
        </div>
      </div>
      <div className="microsoft-source-row__details">
        {source.granted ? (
          asOf && (
            <p className="connector-card__asof-line">
              <Icon icon={Clock3} size={14} />
              {asOf}
            </p>
          )
        ) : (
          <p className="connector-card__empty-body">
            {card.notConnectedBody(label)}
          </p>
        )}
        {isReauth && !frozenHoisted && (
          <p className="connector-card__frozen">
            <Icon icon={LockKeyhole} size={14} />
            {connection.lastSuccessfulSyncAt
              ? card.frozenAt(
                  formatDate(connection.lastSuccessfulSyncAt, locale, ""),
                )
              : card.frozenUnknown}
          </p>
        )}
        {inlineConfiguration && (
          <p
            className="connector-card__configuration"
            data-state={inlineConfiguration.state}
          >
            <Icon
              className={
                inlineConfiguration.state === "loading"
                  ? "sync-refresh-icon"
                  : undefined
              }
              icon={sourceConfigurationIcon(inlineConfiguration)}
              size={14}
            />
            {inlineConfiguration.message}
          </p>
        )}
        {liveProcessing && (
          <ProcessingCountLine counts={liveProcessing} messages={messages} />
        )}
      </div>
      <div className="microsoft-source-row__status">
        <StatusPill
          icon={descriptor.icon}
          // The pill carries this service's own live work count — queued plus in
          // progress — so each row reads what is moving without opening a detail.
          // Failed jobs are not live work and stay off the pill; a service with
          // nothing queued or in flight keeps the bare status word.
          label={
            activeWork > 0
              ? `${descriptor.label} · ${formatNumber(activeWork, locale)}`
              : descriptor.label
          }
          status={descriptor.status}
        />
        {explained && (
          <IconButton
            aria-label={messages.connectors.sourceStatusInfo(label)}
            icon={Info}
            onClick={onExplainStatus}
            size="sm"
          />
        )}
      </div>
      {(toggleable || canManage) && (
        <div className="microsoft-source-row__action">
          {/* Manage sits before the switch in the DOM so it lands to the LEFT of
              the trailing switch: the switch keeps the row's right edge and
              stays aligned with the switches in the rows above, which carry no
              button. */}
          {canManage && (
            <Button
              disabled={pendingAction !== null}
              iconStart={<Icon icon={management.icon} size={15} />}
              loading={
                management.loadingLabel !== undefined &&
                pendingAction === "visibility"
              }
              {...(management.loadingLabel
                ? { loadingLabel: management.loadingLabel }
                : {})}
              onClick={management.onManage}
              size="sm"
              variant={management.variant ?? "secondary"}
            >
              {management.label}
            </Button>
          )}
          {toggleable && (
            <div className="microsoft-source-row__toggle">
              <Switch
                checked={source.enabled ?? true}
                disabled={pendingAction !== null}
                label={messages.connectors.serviceToggle.label(label)}
                onChange={(event) =>
                  onToggleService(source, event.target.checked)
                }
              />
              {servicePending && (
                <span className="obsina-sr-only" role="status">
                  {messages.connectors.serviceToggle.pending(label)}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * The exact provider total for a live run, or null. An upper-bound estimate and
 * an overrun both stay null: the pill shows a ratio only when both of its
 * numbers are real, never a guess dressed as progress.
 */
function exactSyncTotal(summary: ControlPlaneSummary): number | null {
  const progress = summary.syncProgress;
  if (progress?.estimateKind !== "exact") return null;
  const total = progress.estimatedItems;
  if (total === null || total <= 0 || progress.processedItems > total) {
    return null;
  }
  return total;
}

/**
 * The provider pill's label. While a run is live and the provider reported an
 * exact total, the pill carries the inventory ratio and refreshes with the
 * summary; otherwise it stays the plain state word.
 */
function providerStatusLabel(
  summary: ControlPlaneSummary,
  messages: AppMessages,
  locale: string,
): string {
  const base = connectorStatusDescriptor(summary.state, messages).label;
  if (summary.state !== "syncing" || !summary.syncProgress) return base;
  const total = exactSyncTotal(summary);
  return total === null
    ? base
    : messages.connectors.status.syncingProgress(
        formatNumber(summary.syncProgress.processedItems, locale),
        formatNumber(total, locale),
      );
}

/**
 * One provider-level Microsoft 365 surface. Account identity, privacy, overall
 * health and provider actions appear once; the five services stay compact rows
 * with their own authorization, watermark and configuration truth. Counts,
 * timestamps and the long health explanation live in the connection dialog so
 * the card answers who is connected and what it is doing at a glance.
 */
function Microsoft365ConnectorGroup({
  connection,
  messages,
  onExplainSourceStatus,
  onReauthorize,
  onShowConnectionInfo,
  onShowProcessingDetails,
  onShowSyncDetails,
  onToggleService,
  pendingAction,
  sourceConfigurations,
  sourceManagement,
  sources,
  summary,
}: {
  connection: ConnectionSummary;
  messages: AppMessages;
  onExplainSourceStatus: (kind: SourceKind) => void;
  onReauthorize: () => void;
  onShowConnectionInfo: () => void;
  onShowProcessingDetails: () => void;
  onShowSyncDetails: () => void;
  onToggleService: (source: SourceType, enabled: boolean) => void;
  pendingAction: PendingAction | null;
  sourceConfigurations: MicrosoftSourceConfigurations;
  sourceManagement: MicrosoftSourceManagement;
  sources: readonly SourceType[];
  summary: ControlPlaneSummary;
}) {
  const { locale } = useObsinaI18n();
  const descriptor = connectorStatusDescriptor(summary.state, messages);
  const health = connectorHealthCopy(summary, messages);
  const statusHeadingId = `connector-health-${connection.id}`;
  const providerLabel = messages.connectors.provider.microsoft;
  const sourceTypesKnown = connection.sourceTypes !== undefined;
  const connectedSources =
    connection.sourceTypes?.filter((source) => source.granted).length ?? null;
  const totalSources = connection.sourceTypes?.length ?? null;
  // The rows below already name every connected service, so the count earns its
  // place only while some service is missing — the case that needs an action.
  const partialServiceCount =
    connectedSources !== null &&
    totalSources !== null &&
    connectedSources < totalSources
      ? messages.connectors.servicesConnected(connectedSources, totalSources)
      : null;
  // A fact every granted service shares is one line, not five. The rows drop it
  // and the list states it once; a mixed set keeps its per-row truth. The frozen
  // cut-off is connection-level by construction, so it always hoists.
  const grantedSources = sources.filter((source) => source.granted);
  const coveragePendingHoisted =
    grantedSources.length > 1 &&
    grantedSources.every((source) => source.watermark === null);
  const frozenHoisted =
    summary.state === "reauthorization_required" && grantedSources.length > 1;
  const needsAccessUpdate = sources.some((source) => !source.granted);
  const healthIcon =
    healthIconByState[summary.state as keyof typeof healthIconByState] ??
    ShieldCheck;
  const scopePending =
    summary.state === "scope_required"
      ? messages.connectors.scopePendingMessage
      : null;
  const configurationFor = (
    source: SourceType,
  ): SourceConfigurationDescriptor | undefined =>
    source.kind === "mail"
      ? sourceConfigurations.mail
      : source.kind === "onedrive"
        ? sourceConfigurations.onedrive
        : source.kind === "sharepoint"
          ? sourceConfigurations.sharepoint
          : undefined;
  const managementFor = (
    source: SourceType,
  ): SourceManagementDescriptor | undefined =>
    source.kind === "mail"
      ? sourceManagement.mail
      : source.kind === "onedrive"
        ? sourceManagement.onedrive
        : source.kind === "sharepoint"
          ? sourceManagement.sharepoint
          : undefined;
  return (
    <Card
      aria-label={messages.connectors.cardLabel(providerLabel)}
      as="section"
      className="connector-card microsoft365-connector"
      data-status={descriptor.status}
      data-provider="microsoft"
    >
      <div
        className="microsoft365-connector__provider"
        data-layout={summary.state === "syncing" ? "live" : undefined}
      >
        <div className="microsoft365-connector__head">
          <div className="microsoft365-connector__identity">
            <ConnectorGridIcon tone="brand" />
            <div className="connector-card__titles">
              <div className="connector-card__name">{providerLabel}</div>
              <div className="connector-card__src">
                {connection.accountLabel}
              </div>
            </div>
          </div>
          <div className="connector-card__pill">
            <StatusPill
              className="connector-status-pill"
              icon={descriptor.icon}
              label={providerStatusLabel(summary, messages, locale)}
              status={descriptor.status}
            />
            {/* Sits with the pill rather than the health line, because states
                without a health headline (scope pending, empty) still have to
                reach the counts and timestamps the card demoted. */}
            <IconButton
              aria-label={messages.connectors.connectionInfo}
              icon={Info}
              onClick={onShowConnectionInfo}
              size="sm"
            />
          </div>
          <ConnectionVisibilityBadge
            connection={connection}
            messages={messages}
          />
        </div>
        {/* A live sync no longer stacks its own band at the top. The whole
            progress surface — heading, count, bar and ETA — lives once at the
            foot of the card, in the footer slot the backlog line held
            (SyncingSummary). Non-syncing states keep their health headline. */}
        {summary.state !== "syncing" && health && (
          <div className="microsoft365-connector__health" data-tone={health[2]}>
            <span className="connector-card__status-icon">
              <Icon icon={healthIcon} size={16} />
            </span>
            <h2 id={statusHeadingId}>{health[0]}</h2>
          </div>
        )}
        {partialServiceCount && (
          <p className="microsoft365-connector__service-count">
            {partialServiceCount}
          </p>
        )}
        {scopePending && (
          <p className="connector-card__degraded" data-tone={descriptor.tone}>
            <Icon icon={descriptor.icon} size={15} />
            {scopePending}
          </p>
        )}
        {/* The footer carries only the two actions a user genuinely has to
            take, because Obsina cannot resolve them in the background: renewing
            an expired sign-in, and granting access to a service. Everything
            else — checking status, retrying, re-syncing — the self-updating
            surface does on its own, so no user-triggering control is shown. */}
        {summary.state !== "scope_required" &&
          (summary.state === "reauthorization_required" ||
            needsAccessUpdate) && (
            <div className="microsoft365-connector__footer">
              <div className="health-actions">
                {summary.state === "reauthorization_required" ? (
                  <Button
                    disabled={pendingAction !== null}
                    loading={pendingAction === "authorization"}
                    onClick={onReauthorize}
                  >
                    {messages.health.reconnect}
                  </Button>
                ) : (
                  <Button
                    disabled={pendingAction !== null}
                    loading={pendingAction === "authorization"}
                    onClick={onReauthorize}
                    variant="secondary"
                  >
                    {messages.connectors.updateAccess}
                  </Button>
                )}
              </div>
            </div>
          )}
      </div>
      {sourceTypesKnown ? (
        <div className="microsoft365-connector__services">
          <ul
            aria-label={messages.connectors.microsoftServices}
            className="microsoft365-connector__sources"
          >
            {sources.map((source) => (
              <li key={source.kind}>
                <MicrosoftSourceRow
                  configuration={configurationFor(source)}
                  connection={connection}
                  coveragePendingHoisted={coveragePendingHoisted}
                  frozenHoisted={frozenHoisted}
                  management={managementFor(source)}
                  messages={messages}
                  onExplainStatus={() => onExplainSourceStatus(source.kind)}
                  onToggleService={onToggleService}
                  pendingAction={pendingAction}
                  source={source}
                  summary={summary}
                />
              </li>
            ))}
          </ul>
          {frozenHoisted && (
            <p className="microsoft365-connector__coverage-note">
              <Icon icon={LockKeyhole} size={14} />
              {connection.lastSuccessfulSyncAt
                ? messages.connectors.sourceCard.frozenAt(
                    formatDate(connection.lastSuccessfulSyncAt, locale, ""),
                  )
                : messages.connectors.sourceCard.frozenUnknown}
            </p>
          )}
          {coveragePendingHoisted && (
            <p className="microsoft365-connector__coverage-note">
              <Icon icon={Clock3} size={14} />
              {messages.connectors.sourceCard.coveragePendingAll}
            </p>
          )}
        </div>
      ) : (
        <p className="microsoft365-connector__services-unknown">
          <Icon icon={CircleHelp} size={15} />
          {messages.connectors.servicesUnavailable}
        </p>
      )}
      {/* Stays outside the row list: a connection whose services are unknown
          still has a backlog state worth stating. */}
      <ProcessingSummaryLine
        connection={connection}
        headingId={statusHeadingId}
        messages={messages}
        onShowDetails={onShowProcessingDetails}
        onShowSyncDetails={onShowSyncDetails}
        sources={sources}
        summary={summary}
        syncHeadline={health?.[0] ?? messages.syncProgress.title}
      />
    </Card>
  );
}

interface ConnectedDetailProps {
  apiBase: string | null;
  messages: AppMessages;
  metricDetails: MetricKey | null;
  onHistoryStartChange: (historyStartAt: string | null) => void;
  onShowMetricDetails: (metric: MetricKey) => void;
  pendingAction: PendingAction | null;
  summary: ControlPlaneSummary;
}

/**
 * The connection's backlog, totalled. `processingFlow` is the authoritative
 * safe aggregate; when a summary carries per-source counts without it, those
 * plus the unattributed remainder are the total instead. Null means no read
 * surface reported anything — an honest unknown, never a fabricated zero.
 */
function processingTotals(
  summary: ControlPlaneSummary,
  connection: ConnectionSummary,
  sources: readonly SourceType[],
): SourceProcessing | null {
  const flow = summary.processingFlow;
  if (flow) {
    return flow.stages.reduce<SourceProcessing>(
      (accumulated, stage) => ({
        queued: accumulated.queued + stage.pending,
        inProgress: accumulated.inProgress + stage.processing,
        failed: accumulated.failed + stage.failed,
      }),
      { queued: 0, inProgress: 0, failed: 0 },
    );
  }
  const reported = [
    ...sources.map((source) => source.processing),
    connection.processingUnattributed,
  ].filter((counts): counts is SourceProcessing => counts !== undefined);
  return reported.length === 0 ? null : reported.reduce(addWork);
}

/**
 * The service list's own footer: how much work this connection has, stated in
 * the same card as the rows that carry it. The three states stay distinct in
 * words — unknown (nothing reported), idle (reported and empty), and live
 * counts — so a missing read surface never reads as an empty queue. The stage
 * breakdown the rows cannot hold lives behind the info control.
 */
function ProcessingSummaryLine({
  connection,
  headingId,
  messages,
  onShowDetails,
  onShowSyncDetails,
  sources,
  summary,
  syncHeadline,
}: {
  connection: ConnectionSummary;
  headingId: string;
  messages: AppMessages;
  onShowDetails: () => void;
  onShowSyncDetails: () => void;
  sources: readonly SourceType[];
  summary: ControlPlaneSummary;
  syncHeadline: string;
}) {
  // A live sync owns this footer slot: the compact progress surface stands in
  // for the backlog counts, which are internal and move behind the detail
  // control while the run is active.
  if (summary.state === "syncing") {
    return (
      <SyncingSummary
        headingId={headingId}
        headline={syncHeadline}
        messages={messages}
        onShowDetails={onShowSyncDetails}
        summary={summary}
      />
    );
  }
  const copy = messages.processing;
  const totals = processingTotals(summary, connection, sources);
  const unattributed = connection.processingUnattributed;
  return (
    <section
      aria-label={copy.regionLabel}
      className="microsoft365-connector__processing"
    >
      {/* Four states, four sentences: nothing was reported, work is moving,
          work has stopped, there is no work. The third used to be printed as
          the fourth — the footer read "nothing queued or in progress" over a
          connection whose lane had been dead for a day — so stopped work now
          says so here, in the same card, beside whatever is still moving. */}
      {totals === null ? (
        <p className="microsoft365-connector__processing-state">
          <Icon icon={CircleHelp} size={14} />
          {copy.unknown}
        </p>
      ) : hasLiveWork(totals) ? (
        <ProcessingCountLine counts={totals} labelled messages={messages} />
      ) : hasStoppedWork(totals) ? (
        <p
          className="microsoft365-connector__processing-state"
          data-tone="danger"
        >
          <Icon icon={AlertTriangle} size={14} />
          {copy.stopped(totals.failed)}
        </p>
      ) : (
        <p className="microsoft365-connector__processing-state">
          <Icon icon={Check} size={14} />
          {copy.idle}
        </p>
      )}
      {totals !== null && hasLiveWork(totals) && hasStoppedWork(totals) && (
        <p
          className="microsoft365-connector__processing-note"
          data-tone="danger"
        >
          {copy.stopped(totals.failed)}
        </p>
      )}
      {unattributed &&
        (hasLiveWork(unattributed) || hasStoppedWork(unattributed)) && (
          <p className="microsoft365-connector__processing-note">
            {copy.unattributed(totalWork(unattributed))}
          </p>
        )}
      <IconButton
        aria-label={copy.detailLabel}
        icon={Info}
        onClick={onShowDetails}
        size="sm"
      />
    </section>
  );
}

/**
 * The pipeline detail the service rows cannot hold: per-stage queue depth,
 * recently completed work, the backlog's as-of time, and the jobs that are not
 * attributed to one service. It draws only from the summary's safe-aggregate
 * `processingFlow` — counts, never raw errors. An absent flow stays an explicit
 * "unknown"; a present-but-empty flow is an honest "idle". Status is conveyed
 * by text and icon, not colour alone.
 */
function ProcessingDetailDialog({
  connection,
  messages,
  onOpenChange,
  open,
  summary,
}: {
  connection: ConnectionSummary | null;
  messages: AppMessages;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  summary: ControlPlaneSummary;
}) {
  const { locale } = useObsinaI18n();
  const copy = messages.processing;
  const flow = summary.processingFlow;
  const unattributed = connection?.processingUnattributed;
  const totals = flow
    ? flow.stages.reduce(
        (accumulated, stage) => ({
          pending: accumulated.pending + stage.pending,
          processing: accumulated.processing + stage.processing,
          recentCompleted: accumulated.recentCompleted + stage.recentCompleted,
          failed: accumulated.failed + stage.failed,
        }),
        { pending: 0, processing: 0, recentCompleted: 0, failed: 0 },
      )
    : null;
  const activeStages =
    flow?.stages.filter(
      (stage) => stage.pending + stage.processing + stage.failed > 0,
    ) ?? [];
  return (
    <Dialog
      description={copy.dialogBody}
      onOpenChange={onOpenChange}
      open={open}
      size="sm"
      title={copy.title}
    >
      {flow && totals ? (
        <>
          <p className="processing-panel__asof">
            <Icon icon={Clock3} size={13} />
            {copy.asOf(formatDate(flow.asOf, locale, ""))}
          </p>
          {totals.pending === 0 &&
          totals.processing === 0 &&
          totals.failed === 0 ? (
            <p className="processing-panel__idle">{copy.idle}</p>
          ) : (
            <dl className="processing-panel__totals">
              <div>
                <dt>{copy.queued}</dt>
                <dd>{totals.pending}</dd>
              </div>
              <div>
                <dt>{copy.inFlight}</dt>
                <dd>{totals.processing}</dd>
              </div>
              <div>
                <dt>{copy.recent}</dt>
                <dd>{totals.recentCompleted}</dd>
              </div>
              {totals.failed > 0 && (
                <div data-tone="danger">
                  <dt>{copy.failed}</dt>
                  <dd>{totals.failed}</dd>
                </div>
              )}
            </dl>
          )}
          {activeStages.length > 0 && (
            <ul className="processing-panel__stages">
              {activeStages.map((stage) => (
                <li key={stage.kind}>
                  <span className="processing-panel__stage-name">
                    {copy.stageLabel[stage.kind] ?? stage.kind}
                  </span>
                  <span className="processing-panel__stage-counts">
                    {stage.pending > 0 && (
                      <span>{copy.stageQueued(stage.pending)}</span>
                    )}
                    {stage.processing > 0 && (
                      <span>
                        <Icon icon={RefreshCw} size={12} />
                        {copy.stageInFlight(stage.processing)}
                      </span>
                    )}
                    {stage.failed > 0 && (
                      <span data-tone="danger">
                        <Icon icon={AlertTriangle} size={12} />
                        {copy.stageFailed(stage.failed)}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <p className="processing-panel__unknown">
          <Icon icon={CircleHelp} size={15} />
          {copy.unknown}
        </p>
      )}
      {/* The detail dialog is the honest, complete surface: it opts into the
          failed count here (and in the totals above), so unattributed work that
          is only failed still shows rather than being dropped. */}
      {unattributed &&
        (hasLiveWork(unattributed) || hasStoppedWork(unattributed)) && (
          <section className="processing-panel__unattributed">
            <h3>{copy.unattributedTitle}</h3>
            <ProcessingCountLine
              counts={unattributed}
              messages={messages}
              showFailed
            />
          </section>
        )}
    </Dialog>
  );
}

/**
 * The preserved per-connection detail surfaces (attention items, history range,
 * full coverage, evidence thread). Their behaviour is unchanged from the single
 * card layout; only the composition moved beneath the connector grid.
 */
function ConnectedDetail({
  apiBase,
  messages,
  onHistoryStartChange,
  onShowMetricDetails,
  pendingAction,
  summary,
}: ConnectedDetailProps) {
  return (
    <div className="control-dashboard">
      {summary.issues.length > 0 && (
        <section aria-labelledby="issues-title" className="issue-list">
          <h2 id="issues-title">{messages.health.issuesTitle}</h2>
          {/* The wording comes from `id`, never from the payload's own `title`
              and `detail`: those are English, composed on the server, and are
              kept on the wire only because consumers other than this app read
              them. `retryable` is behaviour and still comes from the payload. */}
          {summary.issues.map((issue) => (
            <Callout
              key={issue.id}
              title={messages.health.issues[issue.id].title}
              tone={issue.retryable ? "warning" : "danger"}
            >
              {messages.health.issues[issue.id].detail}
            </Callout>
          ))}
        </section>
      )}
      <div className="dashboard-grid">
        <HistorySettings
          apiBase={apiBase}
          disabled={pendingAction !== null}
          messages={messages}
          onSave={onHistoryStartChange}
          pending={pendingAction === "history"}
          summary={summary}
        />
        <CoveragePanel
          messages={messages}
          onShowDetails={onShowMetricDetails}
          summary={summary}
        />
        <EvidenceThread messages={messages} summary={summary} />
      </div>
    </div>
  );
}

function formatClock(value: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(value);
}

function ScopeDecision({
  apiBase,
  summary,
  messages,
  onContinue,
  pending,
}: {
  apiBase: string | null;
  summary: ControlPlaneSummary;
  messages: AppMessages;
  onContinue: (value: VisibilityClass, historyStartAt: string | null) => void;
  pending: boolean;
}) {
  const { locale } = useObsinaI18n();
  const connection = primaryConnection(summary);
  const history = useHistoryDraft(
    connection?.historyStartAt ?? historyDateForDays(90),
    connection?.historyStartAt ? undefined : "90",
  );
  const estimate = useHistoryEstimate(apiBase, summary, history, true);
  const estimateBlocking = isHistoryEstimateBlocking(
    apiBase,
    summary,
    history,
    true,
    estimate,
  );
  return (
    // Capping the card freed the width beside it, so the path strip moves into
    // that space instead of leaving an empty band and pushing itself below the
    // fold. Both halves of the step are then one glance apart.
    <div className="setup-layout">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (history.valid) onContinue("owner_private", history.value);
        }}
      >
        <Card aria-labelledby="setup-title" as="section" className="setup-card">
          <h2 id="setup-title">{messages.scope.title}</h2>
          <p className="setup-card__visibility">
            <Icon icon={LockKeyhole} size={15} />
            <strong>{messages.scope.privateTitle}</strong>
            <Badge tone="accent">{messages.scope.defaultBadge}</Badge>
            <span className="setup-card__visibility-note">
              {messages.scope.privateBody}
            </span>
          </p>
          {/* The range is one group: its label, its control, what it will pull
              and its small print belong together, tighter than the gap that
              separates them from the action. The estimate comes before the small
              print because it is the answer to the control, not a caveat. */}
          <div className="setup-card__range">
            <HistoryFields
              draft={history}
              messages={messages}
              note={null}
              showHint={false}
            />
            <HistoryEstimateNotice
              draft={history}
              estimate={estimate}
              locale={locale}
              messages={messages}
            />
            <p className="history-fields__note">{messages.history.setupNote}</p>
          </div>
          <div className="setup-card__submit">
            <Button
              disabled={!history.valid || estimateBlocking}
              loading={pending}
              type="submit"
            >
              {messages.scope.submit}
            </Button>
            <p className="setup-card__footnote">
              {messages.scope.noClassification}
            </p>
          </div>
        </Card>
      </form>
      <EvidenceThread messages={messages} summary={summary} />
    </div>
  );
}

const HISTORY_PRESETS = ["all", "30", "90", "365", "custom"] as const;
type HistoryPreset = (typeof HISTORY_PRESETS)[number];

// Guards the untrusted DOM <select> value before it reaches choosePreset, whose
// numeric branch would otherwise turn a stray non-preset string into
// historyDateForDays(NaN) — an Invalid Date whose toISOString() throws.
export function isHistoryPreset(value: string): value is HistoryPreset {
  return HISTORY_PRESETS.some((preset) => preset === value);
}

interface HistoryDraft {
  preset: HistoryPreset;
  value: string | null;
  customDate: string;
  dirty: boolean;
  valid: boolean;
  choosePreset: (preset: HistoryPreset) => void;
  chooseCustomDate: (value: string) => void;
}

function useHistoryDraft(
  initialValue: string | null,
  initialPreset?: HistoryPreset,
): HistoryDraft {
  const [preset, setPreset] = useState<HistoryPreset>(
    () => initialPreset ?? historyPresetFor(initialValue),
  );
  const [value, setValue] = useState<string | null>(initialValue);

  useEffect(() => {
    setPreset(initialPreset ?? historyPresetFor(initialValue));
    setValue(initialValue);
  }, [initialPreset, initialValue]);

  const choosePreset = (next: HistoryPreset) => {
    setPreset(next);
    if (next === "all") {
      setValue(null);
    } else if (next !== "custom") {
      setValue(historyDateForDays(Number(next)));
    } else if (!value) {
      setValue(historyDateForDays(90));
    }
  };

  const chooseCustomDate = (next: string) => {
    setPreset("custom");
    setValue(dateInputToIso(next));
  };

  return {
    preset,
    value,
    customDate: value?.slice(0, 10) ?? "",
    dirty: value !== initialValue,
    valid: value === null || isAllowedHistoryDate(value),
    choosePreset,
    chooseCustomDate,
  };
}

/**
 * `note` replaces the default small print, and setting `showHint` false folds
 * the select's own hint into it — first-time setup carries one line under the
 * controls instead of two stacked grey sentences.
 */
function HistoryFields({
  draft,
  messages,
  disabled = false,
  note,
  showHint = true,
}: {
  draft: HistoryDraft;
  messages: AppMessages;
  disabled?: boolean;
  /** `null` omits the small print so a caller can place it after the estimate. */
  note?: string | null;
  showHint?: boolean;
}) {
  return (
    <div
      className="history-fields"
      data-custom={draft.preset === "custom" || undefined}
    >
      <Select
        disabled={disabled}
        hint={showHint ? messages.history.hint : undefined}
        label={messages.history.label}
        onChange={(event) => {
          const next = event.currentTarget.value;
          if (isHistoryPreset(next)) draft.choosePreset(next);
        }}
        options={[
          { value: "30", label: messages.history.last30Days },
          { value: "90", label: messages.history.last90Days },
          { value: "365", label: messages.history.lastYear },
          { value: "all", label: messages.history.allHistory },
          { value: "custom", label: messages.history.customDate },
        ]}
        value={draft.preset}
      />
      {draft.preset === "custom" && (
        <Input
          disabled={disabled}
          error={draft.valid ? undefined : messages.history.invalidDate}
          label={messages.history.dateLabel}
          max={todayDateInput()}
          min="2000-01-01"
          onChange={(event) =>
            draft.chooseCustomDate(event.currentTarget.value)
          }
          required
          type="date"
          value={draft.customDate}
        />
      )}
      {note !== null && (
        <p className="history-fields__note">
          {note ?? messages.history.limitNote}
        </p>
      )}
    </div>
  );
}

type HistoryEstimateState =
  | {
      status: "idle" | "checking" | "unavailable";
      value: null;
      requestKey: string | null;
    }
  | {
      status: "ready";
      value: ControlPlaneHistoryEstimate;
      requestKey: string;
    };

function useHistoryEstimate(
  apiBase: string | null,
  summary: ControlPlaneSummary,
  draft: HistoryDraft,
  enabled: boolean,
): HistoryEstimateState {
  const connectionId = primaryConnection(summary)?.id ?? null;
  const requestKey = historyEstimateRequestKey(draft.value);
  const [estimate, setEstimate] = useState<HistoryEstimateState>({
    status: "idle",
    value: null,
    requestKey: null,
  });

  useEffect(() => {
    if (
      !enabled ||
      !apiBase ||
      !connectionId ||
      summary.synthetic ||
      !draft.valid
    ) {
      setEstimate({ status: "idle", value: null, requestKey: null });
      return;
    }
    const controller = new AbortController();
    setEstimate({ status: "checking", value: null, requestKey });
    const timeout = window.setTimeout(() => {
      void requestHistoryEstimate(
        apiBase,
        connectionId,
        draft.value,
        controller.signal,
      )
        .then((value) => {
          if (!controller.signal.aborted) {
            setEstimate({ status: "ready", value, requestKey });
          }
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setEstimate({
              status: "unavailable",
              value: null,
              requestKey,
            });
          }
        });
    }, 350);
    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [
    apiBase,
    connectionId,
    draft.valid,
    draft.value,
    enabled,
    requestKey,
    summary.synthetic,
  ]);

  return estimate;
}

function isHistoryEstimateBlocking(
  apiBase: string | null,
  summary: ControlPlaneSummary,
  draft: HistoryDraft,
  enabled: boolean,
  estimate: HistoryEstimateState,
): boolean {
  if (
    !enabled ||
    !apiBase ||
    !primaryConnection(summary) ||
    summary.synthetic ||
    !draft.valid
  ) {
    return false;
  }
  return (
    estimate.requestKey !== historyEstimateRequestKey(draft.value) ||
    estimate.status === "idle" ||
    estimate.status === "checking"
  );
}

function HistoryEstimateNotice({
  draft,
  estimate,
  locale,
  messages,
}: {
  draft: HistoryDraft;
  estimate: HistoryEstimateState;
  locale: string;
  messages: AppMessages;
}) {
  if (estimate.requestKey !== historyEstimateRequestKey(draft.value)) {
    return null;
  }
  const estimateText =
    estimate.status === "checking"
      ? messages.history.estimateChecking
      : estimate.status === "unavailable"
        ? messages.history.estimateUnavailable
        : estimate.status === "ready"
          ? estimate.value.kind === "exact"
            ? messages.history.estimateExact(
                formatNumber(estimate.value.total, locale),
              )
            : messages.history.estimateUpperBound(
                formatNumber(estimate.value.total, locale),
              )
          : null;
  if (!estimateText) return null;
  return (
    <div
      aria-live="polite"
      className="history-estimate"
      data-state={estimate.status}
    >
      <span aria-hidden="true" />
      <strong>{formatHistoryRange(draft.value, locale)}</strong>
      <p>{estimateText}</p>
    </div>
  );
}

function historyEstimateRequestKey(value: string | null): string {
  return value ?? "all-available-history";
}

function HistorySettings({
  apiBase,
  summary,
  messages,
  onSave,
  pending,
  disabled,
}: {
  apiBase: string | null;
  summary: ControlPlaneSummary;
  messages: AppMessages;
  onSave: (historyStartAt: string | null) => void;
  pending: boolean;
  disabled: boolean;
}) {
  const { locale } = useObsinaI18n();
  const draft = useHistoryDraft(
    primaryConnection(summary)?.historyStartAt ?? null,
  );
  const estimate = useHistoryEstimate(apiBase, summary, draft, draft.dirty);
  const estimateBlocking = isHistoryEstimateBlocking(
    apiBase,
    summary,
    draft,
    draft.dirty,
    estimate,
  );
  return (
    <Card
      aria-labelledby="history-settings-title"
      as="section"
      className="history-settings"
    >
      <div className="dashboard-card__heading">
        <div>
          <span className="obsina-eyebrow">{messages.history.eyebrow}</span>
          <h2 id="history-settings-title">{messages.history.title}</h2>
        </div>
        <p>{messages.history.body}</p>
      </div>
      <HistoryFields disabled={disabled} draft={draft} messages={messages} />
      <HistoryEstimateNotice
        draft={draft}
        estimate={estimate}
        locale={locale}
        messages={messages}
      />
      <div className="history-settings__footer">
        <Button
          disabled={
            disabled || !draft.dirty || !draft.valid || estimateBlocking
          }
          iconStart={<Icon icon={Save} size={16} />}
          loading={pending}
          loadingLabel={messages.history.saving}
          onClick={() => onSave(draft.value)}
          size="sm"
        >
          {messages.history.save}
        </Button>
        <p>{messages.history.accountSaved}</p>
      </div>
    </Card>
  );
}

type MetricKey = "sources" | "attachments" | "text" | "graph";

function CoveragePanel({
  summary,
  messages,
  onShowDetails,
}: {
  summary: ControlPlaneSummary;
  messages: AppMessages;
  onShowDetails: (metric: MetricKey) => void;
}) {
  const { locale } = useObsinaI18n();
  // Bind the panel's reconciled/incomplete COPY to the same authoritative signal
  // as the header and the status pill — the run-scoped state machine — not to the
  // metric-pure cumulative coverage view. Binding to the latter lets a single
  // persistent all-time terminal failed/partial item print "Coverage is still
  // incomplete" beside the header's "reconciled up to", contradicting itself on
  // one screen. Honesty is preserved below: when the run has reconciled yet
  // terminal residue remains, the copy names that residue as an explicit
  // incomplete status rather than claiming everything is complete.
  const reconciled = summary.state === "healthy";
  const residue = terminalResidueCount(summary.coverage);
  const metrics = [
    [
      "sources",
      messages.coverage.sources,
      messages.coverage.sourcesDetail,
      summary.coverage.sources,
      Folder,
    ],
    [
      "attachments",
      messages.coverage.attachments,
      messages.coverage.attachmentsDetail,
      summary.coverage.attachments,
      Paperclip,
    ],
    [
      "text",
      messages.coverage.text,
      messages.coverage.textDetail,
      summary.coverage.text,
      FileText,
    ],
    [
      "graph",
      messages.coverage.graph,
      messages.coverage.graphDetail,
      summary.coverage.graph,
      Network,
    ],
  ] as const;
  return (
    <Card
      aria-labelledby="coverage-title"
      as="section"
      className="coverage-panel dashboard-card--wide"
    >
      <div className="dashboard-card__heading coverage-panel__heading">
        <div>
          <span className="obsina-eyebrow">{messages.coverage.eyebrow}</span>
          <div className="coverage-panel__title">
            <h2 id="coverage-title">{messages.coverage.title}</h2>
          </div>
        </div>
        <p>{messages.coverage.body}</p>
        <dl className="coverage-panel__window">
          <dt>{messages.coverage.selectedWindowLabel}</dt>
          <dd>
            {formatHistoryRange(
              primaryConnection(summary)?.historyStartAt ?? null,
              locale,
            )}
          </dd>
        </dl>
        <p className="coverage-panel__window-note">
          {messages.coverage.selectedWindowNote}
        </p>
      </div>
      <div className="coverage-list">
        {metrics.map(([key, title, detail, metric, icon]) => (
          <MetricCard
            detail={detail}
            icon={icon}
            key={key}
            messages={messages}
            metric={metric}
            onShowDetails={() => onShowDetails(key)}
            title={title}
          />
        ))}
      </div>
      <div
        className="coverage-watermark"
        data-complete={reconciled || undefined}
      >
        <span className="coverage-watermark__icon">
          <Icon icon={FileCheck2} size={18} />
        </span>
        <div>
          <strong>
            {reconciled
              ? messages.coverage.reconciled
              : messages.coverage.watermark}
          </strong>
          <span>
            {formatOptionalDate(
              summary.coverage.watermark,
              locale,
              messages.coverage.watermarkMissing,
            )}
          </span>
          <p>
            {reconciled
              ? residue > 0
                ? messages.coverage.reconciledResidue(residue)
                : messages.coverage.complete
              : messages.coverage.incomplete}
          </p>
        </div>
      </div>
    </Card>
  );
}

function MetricCard({
  title,
  detail,
  icon,
  metric,
  messages,
  onShowDetails,
}: {
  title: string;
  detail: string;
  icon: LucideIcon;
  metric: CoverageMetric;
  messages: AppMessages;
  onShowDetails: () => void;
}) {
  const { locale } = useObsinaI18n();
  const accounted =
    metric.completed + metric.partial + metric.failed + metric.unsupported;
  const total = metric.total;
  const valueText =
    total === null
      ? accounted > 0
        ? `${formatNumber(accounted, locale)} ${messages.coverage.recorded} · ${messages.coverage.totalPending}`
        : messages.coverage.waiting
      : `${formatNumber(accounted, locale)} ${messages.evidence.of} ${formatNumber(total, locale)} ${messages.coverage.accounted}`;
  return (
    <article className="metric-row">
      <div className="metric-row__head">
        <span className="metric-row__icon">
          <Icon icon={icon} size={16} />
        </span>
        <div className="metric-row__copy">
          <h3>{title}</h3>
          <p>{detail}</p>
        </div>
        <IconButton
          aria-label={messages.coverage.rowDetails(title)}
          icon={ListTree}
          onClick={onShowDetails}
          size="sm"
        />
      </div>
      <div className="metric-row__measure">
        <strong>{valueText}</strong>
        <CoverageSegments metric={metric} />
      </div>
      <CoverageLegend locale={locale} messages={messages} metric={metric} />
    </article>
  );
}

type CoverageSegmentKind = "complete" | "partial" | "failed" | "unsupported";

function coverageSegmentCounts(
  metric: CoverageMetric,
): readonly (readonly [CoverageSegmentKind, number])[] {
  return [
    ["complete", metric.completed],
    ["partial", metric.partial],
    ["failed", metric.failed],
    ["unsupported", metric.unsupported],
  ];
}

/**
 * Decorative proportion bar. It renders only once a total exists, so a run
 * without a known denominator never implies a ratio. The legend beside it
 * carries every count as text, so the segments never encode status by colour
 * alone.
 */
function CoverageSegments({ metric }: { metric: CoverageMetric }) {
  const total = metric.total;
  if (total === null || total <= 0) return null;
  return (
    <div aria-hidden="true" className="coverage-segments">
      {coverageSegmentCounts(metric).map(([kind, count]) =>
        count > 0 ? (
          <span
            data-segment={kind}
            key={kind}
            style={{ inlineSize: `${(count / total) * 100}%` }}
          />
        ) : null,
      )}
    </div>
  );
}

function CoverageLegend({
  metric,
  messages,
  locale,
}: {
  metric: CoverageMetric;
  messages: AppMessages;
  locale: string;
}) {
  const labels: Record<CoverageSegmentKind, string> = {
    complete: messages.coverage.completed,
    partial: messages.coverage.partial,
    failed: messages.coverage.failed,
    unsupported: messages.coverage.unsupported,
  };
  return (
    <dl className="metric-row__states">
      {coverageSegmentCounts(metric).map(([kind, count]) => (
        <div
          data-hot={(kind === "failed" && count > 0) || undefined}
          data-segment={kind}
          key={kind}
        >
          <dt>
            <span aria-hidden="true" className="metric-row__swatch" />
            {labels[kind]}
          </dt>
          <dd>{formatNumber(count, locale)}</dd>
        </div>
      ))}
    </dl>
  );
}

function CoverageMetricDialog({
  summary,
  metricKey,
  messages,
  open,
  onOpenChange,
}: {
  summary: ControlPlaneSummary;
  metricKey: MetricKey | null;
  messages: AppMessages;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { locale } = useObsinaI18n();
  if (!metricKey) return null;
  const configuration = {
    sources: [
      messages.coverage.sources,
      messages.coverage.sourceData,
      summary.coverage.sources,
    ],
    attachments: [
      messages.coverage.attachments,
      messages.coverage.attachmentData,
      summary.coverage.attachments,
    ],
    text: [
      messages.coverage.text,
      messages.coverage.textData,
      summary.coverage.text,
    ],
    graph: [
      messages.coverage.graph,
      messages.coverage.graphData,
      summary.coverage.graph,
    ],
  } as const;
  const [title, dataDescription, metric] = configuration[metricKey];
  const accounted =
    metric.completed + metric.partial + metric.failed + metric.unsupported;
  const pending =
    metric.total === null ? null : Math.max(0, metric.total - accounted);
  const values = [
    [messages.coverage.completed, metric.completed],
    [messages.coverage.partial, metric.partial],
    [messages.coverage.failed, metric.failed],
    [messages.coverage.unsupported, metric.unsupported],
    [messages.coverage.pending, pending],
    [messages.coverage.total, metric.total],
  ] as const;
  return (
    <Dialog
      description={messages.coverage.detailsBody}
      onOpenChange={onOpenChange}
      open={open}
      title={messages.coverage.detailsTitle(title)}
    >
      <div className="coverage-detail__data">
        <strong>{messages.coverage.processedData}</strong>
        <p>{dataDescription}</p>
      </div>
      <dl className="coverage-detail__counts">
        {values.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>
              {value === null
                ? messages.common.notAvailable
                : formatNumber(value, locale)}
            </dd>
          </div>
        ))}
      </dl>
    </Dialog>
  );
}

/**
 * The one band a live run gets. It carries the connection's health headline, so
 * the card never stacks a state box above a progress box saying the same thing.
 * The count that is real leads; an upper bound follows it as a quieter, clearly
 * labelled estimate, and a range with no estimate says so rather than borrowing
 * a number from an earlier one. Elapsed and remaining time are pipeline detail
 * and stay in the sync-details dialog.
 */
/**
 * A client-side remaining-time estimate for a live sync. It samples the
 * processed count over the last few polls and projects the tail from the
 * measured throughput — a derived hint, never a claimed latency. Returns null
 * (so the caller says it is still estimating) until at least two samples show
 * real forward motion, and whenever the total is unknown or already reached.
 */
function useThroughputEstimate(
  processed: number,
  estimatedTotal: number | null,
): number | null {
  const samplesRef = useRef<{ processed: number; at: number }[]>([]);
  const [etaMs, setEtaMs] = useState<number | null>(null);
  useEffect(() => {
    if (estimatedTotal === null || processed >= estimatedTotal) {
      samplesRef.current = [];
      setEtaMs(null);
      return;
    }
    const now = Date.now();
    const samples = samplesRef.current;
    if (
      samples.length === 0 ||
      samples[samples.length - 1].processed !== processed
    ) {
      samples.push({ processed, at: now });
    }
    // A short window keeps the estimate on current throughput, not the whole
    // run's running average.
    while (samples.length > 6) samples.shift();
    const first = samples[0];
    const advanced = processed - first.processed;
    const spanMs = now - first.at;
    if (samples.length < 2 || advanced <= 0 || spanMs <= 0) {
      setEtaMs(null);
      return;
    }
    setEtaMs(((estimatedTotal - processed) / advanced) * spanMs);
  }, [processed, estimatedTotal]);
  return etaMs;
}

function formatEtaDuration(etaMs: number, messages: AppMessages): string {
  const totalSeconds = Math.max(1, Math.round(etaMs / 1000));
  if (totalSeconds < 90) {
    return messages.syncProgress.durationSeconds(totalSeconds);
  }
  return messages.syncProgress.durationMinutes(Math.round(totalSeconds / 60));
}

/**
 * The live-sync surface, compact. It replaces both the old top banner and the
 * backlog line while a sync runs: one heading, the real count, a progress bar
 * (determinate against a known total, indeterminate while discovering) and a
 * client-derived ETA. No "Check now" — the view polls itself — and no internal
 * queue counts, which stay behind the detail control.
 */
function SyncingSummary({
  headingId,
  headline,
  summary,
  messages,
  onShowDetails,
}: {
  headingId: string;
  headline: string;
  summary: ControlPlaneSummary;
  messages: AppMessages;
  onShowDetails: () => void;
}) {
  const { locale } = useObsinaI18n();
  const processed =
    summary.syncProgress?.processedItems ??
    summary.coverage.sources.completed +
      summary.coverage.sources.partial +
      summary.coverage.sources.failed +
      summary.coverage.sources.unsupported;
  const estimatedItems = summary.syncProgress?.estimatedItems ?? null;
  const estimateKind = summary.syncProgress?.estimateKind ?? null;
  const inventoryMode = summary.syncProgress?.inventoryMode ?? null;
  const estimateExceeded =
    estimatedItems !== null && processed > estimatedItems;
  const exactTotal =
    estimateKind === "exact" && !estimateExceeded ? estimatedItems : null;
  const volumeLabel =
    inventoryMode === "incremental"
      ? messages.syncProgress.incrementalItems(formatNumber(processed, locale))
      : estimateExceeded
        ? messages.syncProgress.estimateExceeded(
            formatNumber(processed, locale),
          )
        : exactTotal !== null
          ? messages.syncProgress.exactProgress(
              formatNumber(processed, locale),
              formatNumber(exactTotal, locale),
            )
          : messages.syncProgress.discoveredItems(
              formatNumber(processed, locale),
            );
  // The bar is determinate only against an exact total — never against an
  // upper-bound estimate, which must not read as exact progress, and never
  // while discovering. Those cases run indeterminate rather than drawing a
  // fraction of a whole the run has not actually measured.
  const barTotal = exactTotal !== null && exactTotal > 0 ? exactTotal : null;
  const etaMs = useThroughputEstimate(processed, barTotal);
  const etaLabel =
    barTotal === null || etaMs === null
      ? messages.syncProgress.estimatingRemaining
      : messages.syncProgress.remainingApprox(
          formatEtaDuration(etaMs, messages),
        );
  return (
    <section
      aria-busy="true"
      aria-labelledby={headingId}
      className="microsoft365-connector__processing sync-summary"
      data-tone="info"
    >
      <div className="sync-summary__line">
        <span className="connector-card__status-icon">
          <Icon icon={RefreshCw} size={14} />
        </span>
        <h2 className="sync-summary__headline" id={headingId}>
          {headline}
        </h2>
        <span className="sync-summary__volume">{volumeLabel}</span>
        <IconButton
          aria-label={messages.syncProgress.detailsLabel}
          icon={CircleHelp}
          onClick={onShowDetails}
          size="sm"
        />
      </div>
      <Progress
        className="sync-summary__bar"
        label={messages.syncProgress.title}
        max={barTotal ?? undefined}
        showValue={false}
        value={barTotal === null ? undefined : processed}
        valueLabel={volumeLabel}
      />
      <p className="sync-summary__eta">{etaLabel}</p>
    </section>
  );
}

/**
 * The provider detail the connector card no longer stacks: current state,
 * service count, the sync timestamps and the full health explanation. Every
 * value is read from the summary; an unavailable one stays explicitly
 * unavailable rather than defaulting to a number.
 */
function ConnectionStatusDialog({
  checkedAt,
  connection,
  messages,
  onOpenChange,
  open,
  summary,
}: {
  checkedAt: Date | null;
  connection: ConnectionSummary;
  messages: AppMessages;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  summary: ControlPlaneSummary;
}) {
  const { locale } = useObsinaI18n();
  const health = connectorHealthCopy(summary, messages);
  const connected =
    connection.sourceTypes?.filter((source) => source.granted).length ?? null;
  const total = connection.sourceTypes?.length ?? null;
  const rows: readonly (readonly [string, string])[] = [
    [
      messages.connectors.connectionStateLabel,
      connectorStatusDescriptor(summary.state, messages).label,
    ],
    [
      messages.connectors.connectionServicesLabel,
      connected !== null && total !== null
        ? messages.connectors.servicesConnected(connected, total)
        : messages.connectors.servicesUnavailable,
    ],
    [
      messages.connectors.connectionLastSuccessLabel,
      connection.lastSuccessfulSyncAt
        ? formatDate(connection.lastSuccessfulSyncAt, locale, "")
        : messages.connectors.lastSuccessNever,
    ],
    [
      messages.connectors.connectionCheckedLabel,
      checkedAt
        ? messages.health.checked(formatClock(checkedAt, locale))
        : messages.health.notChecked,
    ],
  ];
  return (
    <Dialog
      description={messages.connectors.connectionDialogBody}
      onOpenChange={onOpenChange}
      open={open}
      title={messages.connectors.connectionDialogTitle}
    >
      {health && (
        <div className="connection-detail__health" data-tone={health[2]}>
          <strong>{health[0]}</strong>
          <p>{health[1]}</p>
        </div>
      )}
      <dl className="coverage-detail__counts">
        {rows.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </Dialog>
  );
}

/**
 * The sentence behind a service row's info control. The status pill already
 * names the state; this explains it and what resolves it, so the row keeps a
 * standing warning panel out of the layout.
 */
function SourceStatusDialog({
  configuration,
  messages,
  onOpenChange,
  open,
  sourceKind,
}: {
  configuration: SourceConfigurationDescriptor | undefined;
  messages: AppMessages;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  sourceKind: SourceKind | null;
}) {
  if (!sourceKind || !configuration) return null;
  return (
    <Dialog
      onOpenChange={onOpenChange}
      open={open}
      size="sm"
      title={messages.connectors.sourceStatusDialogTitle(
        messages.connectors.sourceType[sourceKind],
      )}
    >
      <p className="source-status-detail">{configuration.message}</p>
    </Dialog>
  );
}

function SyncDetailsDialog({
  asOf,
  summary,
  messages,
  open,
  onOpenChange,
}: {
  asOf: Date;
  summary: ControlPlaneSummary;
  messages: AppMessages;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { locale } = useObsinaI18n();
  const processed =
    summary.syncProgress?.processedItems ?? summary.coverage.sources.completed;
  const estimatedItems = summary.syncProgress?.estimatedItems ?? null;
  const estimateKind = summary.syncProgress?.estimateKind ?? null;
  const inventoryMode = summary.syncProgress?.inventoryMode ?? null;
  const estimateExceeded =
    estimatedItems !== null && processed > estimatedItems;
  const exactTotal =
    estimateKind === "exact" && !estimateExceeded ? estimatedItems : null;
  const timing = formatSyncTiming(
    summary.syncProgress?.startedAt ?? null,
    processed,
    exactTotal,
    asOf,
  );
  const stages = syncStageList(summary, messages);
  return (
    <Dialog
      description={messages.syncProgress.detailsBody}
      onOpenChange={onOpenChange}
      open={open}
      title={messages.syncProgress.detailsTitle}
    >
      <dl className="sync-details__summary">
        <div>
          <dt>{messages.syncProgress.selectedRange}</dt>
          <dd>
            {formatHistoryRange(
              summary.syncProgress
                ? summary.syncProgress.historyStartAt
                : (primaryConnection(summary)?.historyStartAt ?? null),
              locale,
            )}
          </dd>
        </div>
        <div>
          <dt>{messages.syncProgress.expectedVolume}</dt>
          <dd>
            {inventoryMode === "incremental"
              ? messages.syncProgress.incrementalVolume
              : estimatedItems === null
                ? messages.syncProgress.estimatePending
                : estimateExceeded
                  ? messages.syncProgress.estimateExceeded(
                      formatNumber(processed, locale),
                    )
                  : estimateKind === "exact"
                    ? messages.syncProgress.exactItems(
                        formatNumber(estimatedItems, locale),
                      )
                    : messages.syncProgress.estimatedItems(
                        formatNumber(estimatedItems, locale),
                      )}
          </dd>
        </div>
        <div>
          <dt>{messages.syncProgress.discoveredSoFar}</dt>
          <dd>
            {inventoryMode === "incremental"
              ? messages.syncProgress.incrementalItems(
                  formatNumber(processed, locale),
                )
              : messages.syncProgress.discoveredItems(
                  formatNumber(processed, locale),
                )}
          </dd>
        </div>
        <div>
          <dt>{messages.syncProgress.timeEstimate}</dt>
          <dd>
            {[timing.elapsed, timing.remaining].filter(Boolean).join(" · ") ||
              (inventoryMode === "incremental"
                ? messages.syncProgress.incrementalTiming
                : messages.syncProgress.estimatingRemaining)}
          </dd>
        </div>
      </dl>
      <ol className="sync-details__stages">
        {stages.map((stage) => (
          <li data-state={stage.state} key={stage.label}>
            <span aria-hidden="true" />
            <strong>{stage.label}</strong>
            <small>{syncStageLabel(stage.state, messages)}</small>
          </li>
        ))}
      </ol>
    </Dialog>
  );
}

type SyncStageState = "waiting" | "active" | "complete";

function syncStageList(
  summary: ControlPlaneSummary,
  messages: AppMessages,
): readonly { label: string; state: SyncStageState }[] {
  const syncing = summary.state === "syncing";
  const phase = summary.syncProgress?.phase ?? "analyzing";
  const inventoryMode = summary.syncProgress?.inventoryMode ?? null;
  return [
    {
      label: messages.syncProgress.stageAnalyze,
      state:
        inventoryMode !== null
          ? "complete"
          : phase === "analyzing"
            ? "active"
            : "waiting",
    },
    {
      label: messages.syncProgress.stageInventory,
      state:
        phase === "inventorying"
          ? "active"
          : phase === "processing" || phase === "complete"
            ? "complete"
            : "waiting",
    },
    {
      label: messages.syncProgress.stageText,
      state: syncStageState(summary.coverage.text, syncing),
    },
    {
      label: messages.syncProgress.stageEvidence,
      state: syncStageState(summary.coverage.graph, syncing),
    },
    {
      // The terminal "Verify coverage" step is a reconciliation claim of the
      // same class as the header pill and as-of line, so it binds to the same
      // authoritative signal — the state machine's `healthy` decision — not to
      // the cumulative coverage view. Binding it to the all-time view instead
      // let a persistent terminal residue keep this step "waiting" forever while
      // the pill read "Up to date", contradicting itself on one screen. The
      // residue stays honestly visible in the text/evidence stages and coverage
      // tiles above; this step only mirrors the verified-healthy state.
      label: messages.syncProgress.stageVerify,
      state: summary.state === "healthy" ? "complete" : "waiting",
    },
  ];
}

function syncStageState(
  metric: CoverageMetric,
  syncing: boolean,
): SyncStageState {
  const accounted =
    metric.completed + metric.partial + metric.failed + metric.unsupported;
  if (metric.total !== null && accounted >= metric.total) return "complete";
  if (syncing && accounted > 0) return "active";
  return "waiting";
}

function syncStageLabel(state: SyncStageState, messages: AppMessages): string {
  if (state === "active") return messages.syncProgress.stageActive;
  if (state === "complete") return messages.syncProgress.stageComplete;
  return messages.syncProgress.stageWaiting;
}

function EvidenceThread({
  summary,
  messages,
}: {
  summary: ControlPlaneSummary;
  messages: AppMessages;
}) {
  const { locale } = useObsinaI18n();
  const connected = Boolean(primaryConnection(summary));
  const waitingScope = summary.state === "scope_required";
  const stages = [
    {
      title: messages.evidence.source,
      icon: Mail,
      value: connected
        ? messages.evidence.connected
        : messages.evidence.notConnected,
      state: connected ? "complete" : "pending",
    },
    {
      title: messages.evidence.inventory,
      icon: Archive,
      value: waitingScope
        ? messages.evidence.waiting
        : connected
          ? metricThreadValue(summary.coverage.sources, locale, messages)
          : messages.evidence.waitingConnection,
      state: stageState(summary.coverage.sources, connected && !waitingScope),
    },
    {
      title: messages.evidence.text,
      icon: FileText,
      value: waitingScope
        ? messages.evidence.waiting
        : connected
          ? metricThreadValue(summary.coverage.text, locale, messages)
          : messages.evidence.waitingConnection,
      state: stageState(summary.coverage.text, connected && !waitingScope),
    },
    {
      title: messages.evidence.evidence,
      icon: Link2,
      value: waitingScope
        ? messages.evidence.waiting
        : connected
          ? metricThreadValue(summary.coverage.graph, locale, messages)
          : messages.evidence.waitingConnection,
      state: stageState(summary.coverage.graph, connected && !waitingScope),
    },
  ];
  // One reason, stated once. Every unfinished stage used to repeat the same
  // sentence, which read as four separate findings and pushed the card past a
  // screenful for no added information.
  const summaryLabel = waitingScope
    ? messages.evidence.waitingScope
    : messages.evidence.stepsComplete(
        stages.filter((stage) => stage.state === "complete").length,
        stages.length,
      );
  return (
    <Card
      aria-labelledby="evidence-thread-title"
      as="section"
      className="evidence-thread"
    >
      <div className="evidence-thread__heading">
        <span>
          <Icon icon={FileCheck2} size={16} />
        </span>
        <h2 id="evidence-thread-title">{messages.evidence.title}</h2>
        <p className="evidence-thread__summary">{summaryLabel}</p>
      </div>
      <ol>
        {stages.map((stage) => (
          <li data-state={stage.state} key={stage.title}>
            <span className="evidence-thread__node">
              <Icon icon={stage.icon} size={16} />
            </span>
            <div>
              <strong>{stage.title}</strong>
              <span>{stage.value}</span>
            </div>
          </li>
        ))}
      </ol>
    </Card>
  );
}

function stageState(
  metric: CoverageMetric,
  active: boolean,
): "complete" | "active" | "pending" | "issue" {
  if (!active) return "pending";
  if (metric.failed > 0 || metric.partial > 0) return "issue";
  if (
    metric.total !== null &&
    metric.completed + metric.partial + metric.unsupported >= metric.total
  )
    return "complete";
  return "active";
}

function metricThreadValue(
  metric: CoverageMetric,
  locale: string,
  messages: AppMessages,
): string {
  return metric.total === null
    ? `${formatNumber(metric.completed, locale)} ${messages.evidence.recorded}`
    : `${formatNumber(metric.completed, locale)} ${messages.evidence.of} ${formatNumber(metric.total, locale)}`;
}

function LoadingState({ label }: { label: string }) {
  return (
    <div
      aria-busy="true"
      aria-label={label}
      className="loading-state"
      role="status"
    >
      <span className="obsina-sr-only">{label}</span>
      <Skeleton height="2.5rem" width="70%" />
      <Skeleton height="8rem" />
      <Skeleton height="18rem" />
    </div>
  );
}

function Unavailable({
  messages,
  onReload,
}: {
  messages: AppMessages;
  onReload: () => void;
}) {
  return (
    <section className="page-stack">
      <h1>{messages.errors.unavailableTitle}</h1>
      <Callout announce title={messages.errors.unavailableTitle} tone="danger">
        <p>{messages.errors.unavailableBody}</p>
        <Button onClick={onReload} size="sm" variant="secondary">
          {messages.health.refresh}
        </Button>
      </Callout>
    </section>
  );
}

function PreviewSwitcher({ label }: { label: string }) {
  return (
    <nav aria-label={label} className="preview-switcher">
      <span>{label}</span>
      {previewStates.map((state) => (
        <a href={`?preview=${state}`} key={state}>
          {state}
        </a>
      ))}
    </nav>
  );
}

function historyPresetFor(value: string | null): HistoryPreset {
  return value === null ? "all" : "custom";
}

function formatNumber(value: number, locale: string): string {
  return new Intl.NumberFormat(locale).format(value);
}

function formatDate(
  value: string | null,
  locale: string,
  fallback: string,
): string {
  if (!value) return fallback;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? fallback
    : new Intl.DateTimeFormat(locale, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(date);
}

function formatOptionalDate(
  value: string | null,
  locale: string,
  fallback: string,
): string {
  return formatDate(value, locale, fallback);
}
